import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { 
  Settings, Users, Calendar, Trophy, Flag, Trash2, PlayCircle, 
  StopCircle, Plus, CheckCircle, ToggleLeft, RefreshCw, RotateCcw,
  UserPlus, Shield, ShieldCheck, Car, ClipboardList, Edit, X,
  Euro, Download, Link, FileText, HelpCircle, BookOpen, Info, 
  AlertTriangle, Zap, Clock, Target, Award, Mail, TestTube2,
  Menu, ChevronDown, UserCheck, UserX, Percent, Star, Calculator, Save,
  LayoutDashboard, AlertCircle, Image, FileDown, RefreshCcw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getRaceFlag, getRaceFlagIcon } from '../utils/countryFlags';
import { formatDriverName, formatDriverNameLastFirst } from '../utils/formatName';
// Import refactored admin tab components
import { 
  DashboardTab, 
  SettingsTab, 
  HelpTab, 
  TestSeasonTab,
  SeasonsTab,
  UsersTab,
  PayoutTab
} from '../components/admin';
import { formatLocalTime } from '../utils/timeUtils';
import CircuitLayoutManager from '../components/admin/CircuitLayoutManager';
import { SprintRecoveryCard } from '../components/admin/SprintRecoveryCard';
import { PointsAuditCard } from '../components/admin/PointsAuditCard';
import {
  getAllSeasons,
  createSeason,
  startSeason,
  closeSeason,
  getAllUsers,
  deleteUser,
  deleteUserSeasonData,
  getRaces,
  getDrivers,
  getTeams,
  enterRaceResults,
  scoreRace,
  rescoreRace,
  resetSeason,
  getSeasonSettings,
  updateSeasonSettings,
  addDriver,
  updateDriver,
  deleteDriver,
  addTeam,
  updateTeam,
  deleteTeam,
  createRace,
  updateRace,
  deleteRace,
  updateUserRole,
  toggleUserActivation,
  adminCreateRacePrediction,
  adminCreateQualifyingPrediction,
  adminGetAllPredictionsForRace,
  exportPredictionsToExcel,
  getFinancialSettings,
  updateFinancialSettings,
  exportUsers,
  checkPayoutCompliance,
  exportUsersCSV,
  sendRaceNewsletter,
  createTestSeason,
  deleteTestSeason,
  getTestSeason,
  getTestRaces,
  getPayoutStructure,
  updatePayoutStructure,
  getChampionshipResults,
  saveChampionshipResults,
  finalizeChampionshipResults,
  adminCreateChampionshipPrediction,
  getAdminDashboardStats,
  mergeDuplicatePredictions,
  syncRaceTimes,
  recalculateLeaderboard,
} from '../services/api';

const Admin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  // Redirect non-admin users
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin' && user.role !== 'super_admin') {
      toast({
        title: "Toegang geweigerd",
        description: "Je hebt geen admin rechten.",
        variant: "destructive"
      });
      navigate('/');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [seasons, setSeasons] = useState([]);
  const [users, setUsers] = useState([]);
  const [races, setRaces] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSeasonYear, setActiveSeasonYear] = useState(2026);

  // Refresh functions for tab components
  const refreshSeasons = async () => {
    try {
      const seasonsData = await getAllSeasons();
      setSeasons(seasonsData);
    } catch (error) {
      console.error('Error refreshing seasons:', error);
    }
  };

  const refreshUsers = async () => {
    try {
      const usersData = await getAllUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Error refreshing users:', error);
    }
  };

  // Season form states
  const [newSeasonYear, setNewSeasonYear] = useState(2027);
  const [closeSeasonYear, setCloseSeasonYear] = useState('');
  const [worldChampion, setWorldChampion] = useState('');
  const [constructorChampion, setConstructorChampion] = useState('');

  // Settings states
  const [settingsSeasonYear, setSettingsSeasonYear] = useState('2026');
  const [seasonSettings, setSeasonSettings] = useState({
    race_top5_enabled: true,
    qualifying_top3_enabled: true,
    qualifying_p2p3_enabled: true,
    pole_time_enabled: true,
    fastest_lap_enabled: true,
    safety_car_enabled: true,
    virtual_safety_car_enabled: true,
    dnf_count_enabled: true,
    rain_race_enabled: true,
    world_champion_enabled: true,
    constructor_champion_enabled: true,
    sprint_race_enabled: true,
    sprint_qualifying_enabled: true,
    sprint_pole_winner_enabled: true
  });

  // Race results form states
  const [selectedRace, setSelectedRace] = useState('');
  const [racePositions, setRacePositions] = useState(Array(22).fill('')); // All 22 positions
  const [dnfDrivers, setDnfDrivers] = useState([]); // Drivers who DNF'd
  const [qualTop3, setQualTop3] = useState(['', '', '']);
  const [fastestLap, setFastestLap] = useState('');
  const [poleTime, setPoleTime] = useState('');
  const [safetyCar, setSafetyCar] = useState('');
  const [virtualSafetyCar, setVirtualSafetyCar] = useState('');
  const [dnfCount, setDnfCount] = useState('');
  
  // Sprint results
  const [sprintWinner, setSprintWinner] = useState('');
  const [sprintPoleWinner, setSprintPoleWinner] = useState('');
  const [sprintPoleTime, setSprintPoleTime] = useState('');
  const [rainRace, setRainRace] = useState('');
  const [fetchingResults, setFetchingResults] = useState(false);
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  
  // Test Season state
  const [testSeason, setTestSeason] = useState(null);
  const [testRaces, setTestRaces] = useState([]);
  const [creatingTestSeason, setCreatingTestSeason] = useState(false);
  const [deletingTestSeason, setDeletingTestSeason] = useState(false);

  // Mobile menu state for admin tabs
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Payout structure state
  const [payoutStructure, setPayoutStructure] = useState(null);
  const [editingPayoutCategory, setEditingPayoutCategory] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutSaving, setPayoutSaving] = useState(false);

  // Championship results state
  const [championshipResults, setChampionshipResults] = useState({
    world_champion: null,
    constructor_champion: null,
    world_champion_top5: [null, null, null, null, null],
    constructor_champion_top5: [null, null, null, null, null],
    finalized: false
  });
  const [selectedWorldChampion, setSelectedWorldChampion] = useState('');
  const [selectedConstructorChampion, setSelectedConstructorChampion] = useState('');
  // Top 5 for championship results (for scoring top 3 predictions)
  const [wdcTop5, setWdcTop5] = useState(['', '', '', '', '']);
  const [wccTop5, setWccTop5] = useState(['', '', '', '', '']);
  const [savingChampionship, setSavingChampionship] = useState(false);

  // Dashboard overview state
  const [dashboardStats, setDashboardStats] = useState({
    racesWithResults: 0,
    racesWithoutResults: 0,
    totalPredictions: 0,
    predictionsPerRace: {}
  });

  // Circuit layouts state
  const [circuitLayouts, setCircuitLayouts] = useState([]);

  // User selection state for bulk delete
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // New driver form
  const [newDriverName, setNewDriverName] = useState('');
  const [newDriverTeam, setNewDriverTeam] = useState('');
  const [newDriverNumber, setNewDriverNumber] = useState('');
  const [newDriverNationality, setNewDriverNationality] = useState('');

  // Edit driver state
  const [editingDriver, setEditingDriver] = useState(null);
  const [editDriverName, setEditDriverName] = useState('');
  const [editDriverTeam, setEditDriverTeam] = useState('');
  const [editDriverNumber, setEditDriverNumber] = useState('');
  const [editDriverNationality, setEditDriverNationality] = useState('');

  // Edit team state
  const [editingTeam, setEditingTeam] = useState(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [editTeamColor, setEditTeamColor] = useState('');

  // New team form
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamColor, setNewTeamColor] = useState('#666666');

  // Race management state
  const [editingRace, setEditingRace] = useState(null);
  const [showAddRace, setShowAddRace] = useState(false);
  const [raceForm, setRaceForm] = useState({
    name: '',
    circuit_name: '',
    location: '',
    country: '',
    date: '',
    time: '',
    fp1_date: '', fp1_time: '',
    fp2_date: '', fp2_time: '',
    fp3_date: '', fp3_time: '',
    qualifying_date: '', qualifying_time: '',
    is_sprint_weekend: false,
    sprint_qualifying_date: '', sprint_qualifying_time: '',
    sprint_date: '', sprint_time: '',
    circuit_layout_id: '', // Selected circuit layout
  });

  // Admin prediction form (for other users)
  const [adminPredUser, setAdminPredUser] = useState('');
  const [adminPredRace, setAdminPredRace] = useState('');
  const [adminTop5, setAdminTop5] = useState(['', '', '', '', '']);
  const [adminQualifying, setAdminQualifying] = useState(['', '', '']);
  const [adminFastestLap, setAdminFastestLap] = useState('');
  const [adminPoleTime, setAdminPoleTime] = useState('');
  const [adminSafetyCar, setAdminSafetyCar] = useState('');
  const [adminVSC, setAdminVSC] = useState('');
  const [adminDnfCount, setAdminDnfCount] = useState('');
  const [adminRainRace, setAdminRainRace] = useState('');
  const [adminSprintWinner, setAdminSprintWinner] = useState('');
  const [adminSprintPoleWinner, setAdminSprintPoleWinner] = useState('');
  const [adminSprintPoleTime, setAdminSprintPoleTime] = useState('');

  // Admin championship prediction form
  const [adminChampUser, setAdminChampUser] = useState('');
  const [adminChampWC, setAdminChampWC] = useState(['', '', '']);
  const [adminChampCC, setAdminChampCC] = useState(['', '', '']);

  // View predictions state
  const [viewPredictionsRace, setViewPredictionsRace] = useState('');
  const [racePredictions, setRacePredictions] = useState([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);

  // Financial settings state
  const [financialSettings, setFinancialSettings] = useState({
    cost_per_race: 1.0,
    admin_cost: 1.0,
    yearly_bonus: 0.0,
    payment_link: ''
  });
  const [savingFinancial, setSavingFinancial] = useState(false);
  const [complianceCheck, setComplianceCheck] = useState(null);
  const [checkingCompliance, setCheckingCompliance] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);

  // Edit prediction state
  const [editingPrediction, setEditingPrediction] = useState(null);
  const [editTop5, setEditTop5] = useState(['', '', '', '', '']);
  const [editQualifying, setEditQualifying] = useState(['', '', '']);
  const [editFastestLap, setEditFastestLap] = useState('');
  const [editPoleTime, setEditPoleTime] = useState('');
  const [editSafetyCar, setEditSafetyCar] = useState('');
  const [editVSC, setEditVSC] = useState('');
  const [editDnfCount, setEditDnfCount] = useState('');
  const [editRainRace, setEditRainRace] = useState('');
  const [editSprintWinner, setEditSprintWinner] = useState('');
  const [editSprintPoleWinner, setEditSprintPoleWinner] = useState('');
  const [editSprintPoleTime, setEditSprintPoleTime] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const openEditModal = (prediction) => {
    setEditingPrediction(prediction);
    
    // Load race prediction data
    if (prediction.race_prediction) {
      const rp = prediction.race_prediction;
      // Convert driver names back to IDs for the dropdowns
      const driverNameToId = {};
      drivers.forEach(d => { driverNameToId[d.name] = d.driver_id; });
      
      setEditTop5(rp.top5?.map(name => driverNameToId[name] || name) || ['', '', '', '', '']);
      setEditFastestLap(driverNameToId[rp.fastest_lap] || rp.fastest_lap || '');
      setEditSafetyCar(rp.safety_car === true || rp.safety_car === 'ja' ? 'ja' : rp.safety_car === false || rp.safety_car === 'nee' ? 'nee' : '');
      setEditVSC(rp.virtual_safety_car === true || rp.virtual_safety_car === 'ja' ? 'ja' : rp.virtual_safety_car === false || rp.virtual_safety_car === 'nee' ? 'nee' : '');
      setEditDnfCount(rp.dnf_count?.toString() || '');
      setEditRainRace(rp.rain_race === true || rp.rain_race === 'ja' ? 'ja' : rp.rain_race === false || rp.rain_race === 'nee' ? 'nee' : '');
      setEditSprintWinner(driverNameToId[rp.sprint_winner] || rp.sprint_winner || '');
      setEditSprintPoleWinner(driverNameToId[rp.sprint_pole_winner] || rp.sprint_pole_winner || '');
      setEditSprintPoleTime(rp.sprint_pole_time || '');
    }
    
    // Load qualifying prediction data
    if (prediction.qualifying_prediction) {
      const qp = prediction.qualifying_prediction;
      const driverNameToId = {};
      drivers.forEach(d => { driverNameToId[d.name] = d.driver_id; });
      
      setEditQualifying(qp.qualifying?.map(name => driverNameToId[name] || name) || ['', '', '']);
      setEditPoleTime(qp.pole_time || '');
    }
  };

  const closeEditModal = () => {
    setEditingPrediction(null);
    setEditTop5(['', '', '', '', '']);
    setEditQualifying(['', '', '']);
    setEditFastestLap('');
    setEditPoleTime('');
    setEditSafetyCar('');
    setEditVSC('');
    setEditDnfCount('');
    setEditRainRace('');
    setEditSprintWinner('');
    setEditSprintPoleWinner('');
    setEditSprintPoleTime('');
  };

  const handleSaveEdit = async () => {
    if (!editingPrediction) return;
    
    setSavingEdit(true);
    try {
      // Save race prediction
      await adminCreateRacePrediction(
        editingPrediction.user_id,
        viewPredictionsRace,
        editTop5[0],
        editTop5,
        editFastestLap,
        editSafetyCar,
        editVSC,
        editDnfCount,
        editRainRace,
        editSprintWinner,
        editSprintPoleWinner,
        editSprintPoleTime
      );
      
      // Save qualifying prediction
      await adminCreateQualifyingPrediction(
        editingPrediction.user_id,
        viewPredictionsRace,
        editQualifying,
        editPoleTime
      );
      
      toast({
        title: 'Voorspelling bijgewerkt',
        description: `Voorspelling van ${editingPrediction.username} is succesvol bijgewerkt`,
      });
      
      // Reload predictions
      await loadPredictionsForRace(viewPredictionsRace);
      closeEditModal();
    } catch (error) {
      console.error('Error saving edit:', error);
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon voorspelling niet opslaan',
        variant: 'destructive',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const loadPredictionsForRace = async (raceId) => {
    if (!raceId) {
      setRacePredictions([]);
      return;
    }
    
    setLoadingPredictions(true);
    try {
      const predictions = await adminGetAllPredictionsForRace(raceId);
      setRacePredictions(predictions);
    } catch (error) {
      console.error('Error loading predictions:', error);
      setRacePredictions([]);
      toast({
        title: 'Fout',
        description: 'Kon voorspellingen niet laden',
        variant: 'destructive',
      });
    } finally {
      setLoadingPredictions(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First, get seasons to find the active one
        const seasonsData = await getAllSeasons().catch(() => []);
        const activeSeason = seasonsData.find(s => s.is_active);
        const activeYear = activeSeason?.year || 2026;
        
        const [usersData, racesData, driversData, teamsData, financialData, dashboardData] = await Promise.all([
          getAllUsers().catch(() => []),
          getRaces(activeYear).catch((err) => { console.error('Error loading races:', err); return []; }),
          getDrivers(activeYear).catch(() => []),
          getTeams(activeYear).catch(() => []),
          getFinancialSettings(activeYear).catch(() => ({ cost_per_race: 1.0, admin_cost: 1.0, payment_link: '' })),
          getAdminDashboardStats(activeYear).catch(() => ({})),
        ]);
        
        console.log('Active season:', activeYear, '- Races loaded:', racesData?.length, 'races');
        if (racesData?.length > 0) {
          console.log('First race has results:', !!racesData[0]?.results);
        }
        
        setSeasons(seasonsData);
        setActiveSeasonYear(activeYear);
        setUsers(usersData);
        setTotalUsers(usersData.length);
        setRaces(racesData || []);
        // Sort drivers alphabetically by last name
        const sortedDrivers = [...driversData].sort((a, b) => {
          const lastNameA = a.name.split(' ').pop().toUpperCase();
          const lastNameB = b.name.split(' ').pop().toUpperCase();
          return lastNameA.localeCompare(lastNameB, 'nl');
        });
        setDrivers(sortedDrivers);
        // Sort teams alphabetically
        const sortedTeams = [...teamsData].sort((a, b) => a.name.localeCompare(b.name, 'nl'));
        setTeams(sortedTeams);
        setFinancialSettings({
          cost_per_race: financialData.cost_per_race || 1.0,
          admin_cost: financialData.admin_cost || 1.0,
          yearly_bonus: financialData.yearly_bonus || 0.0,
          payment_link: financialData.payment_link || ''
        });
        setDashboardStats(dashboardData);
        
        // Fetch circuit layouts
        try {
          const token = localStorage.getItem('token');
          const layoutsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/circuits`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (layoutsResponse.ok) {
            const layouts = await layoutsResponse.json();
            // Sort alphabetically by original filename
            const sortedLayouts = [...layouts].sort((a, b) => 
              (a.original_filename || a.filename || '').toLowerCase()
                .localeCompare((b.original_filename || b.filename || '').toLowerCase(), 'nl')
            );
            setCircuitLayouts(sortedLayouts);
          }
        } catch (err) {
          console.error('Error fetching circuit layouts:', err);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchTestSeason = async () => {
      try {
        const [testSeasonData, testRacesData] = await Promise.all([
          getTestSeason().catch(() => null),
          getTestRaces().catch(() => [])
        ]);
        setTestSeason(testSeasonData);
        setTestRaces(testRacesData);
      } catch (error) {
        console.error('Error fetching test season:', error);
      }
    };

    fetchData();
    fetchTestSeason();
  }, []);

  // Load championship results
  useEffect(() => {
    const loadChampionshipResults = async () => {
      try {
        const results = await getChampionshipResults(2026);
        setChampionshipResults(results);
        setSelectedWorldChampion(results.world_champion || '');
        setSelectedConstructorChampion(results.constructor_champion || '');
        // Load top 5 arrays
        if (results.world_champion_top5) {
          setWdcTop5(results.world_champion_top5.map(v => v || ''));
        } else if (results.world_champion) {
          setWdcTop5([results.world_champion, '', '', '', '']);
        }
        if (results.constructor_champion_top5) {
          setWccTop5(results.constructor_champion_top5.map(v => v || ''));
        } else if (results.constructor_champion) {
          setWccTop5([results.constructor_champion, '', '', '', '']);
        }
      } catch (error) {
        console.error('Error loading championship results:', error);
      }
    };
    loadChampionshipResults();
  }, []);

  // Load settings when season year changes
  useEffect(() => {
    const loadSettings = async () => {
      if (settingsSeasonYear) {
        try {
          const [settings, financial] = await Promise.all([
            getSeasonSettings(parseInt(settingsSeasonYear)),
            getFinancialSettings(parseInt(settingsSeasonYear)).catch(() => ({ cost_per_race: 1.0, admin_cost: 1.0, yearly_bonus: 0.0, payment_link: '' }))
          ]);
          setSeasonSettings(settings);
          setFinancialSettings({
            cost_per_race: financial.cost_per_race || 1.0,
            admin_cost: financial.admin_cost || 1.0,
            yearly_bonus: financial.yearly_bonus || 0.0,
            payment_link: financial.payment_link || ''
          });
        } catch (error) {
          console.error('Error loading settings:', error);
        }
      }
    };
    loadSettings();
  }, [settingsSeasonYear]);

  // Run compliance check
  const runComplianceCheck = async (participants = null, costPerRace = null, racesCount = 24) => {
    setCheckingCompliance(true);
    try {
      const numParticipants = participants || totalUsers || users.length;
      const cost = costPerRace || financialSettings.cost_per_race || 1.0;
      const result = await checkPayoutCompliance(numParticipants, cost, racesCount);
      setComplianceCheck(result);
      return result;
    } catch (error) {
      console.error('Error checking compliance:', error);
      setComplianceCheck(null);
      return null;
    } finally {
      setCheckingCompliance(false);
    }
  };

  // Handle financial settings update
  const handleUpdateFinancialSettings = async () => {
    setSavingFinancial(true);
    try {
      await updateFinancialSettings(parseInt(settingsSeasonYear), financialSettings);
      toast({
        title: 'Opgeslagen',
        description: 'Financiële instellingen zijn bijgewerkt',
      });
      
      // Run compliance check after saving
      const participants = totalUsers || users.length;
      if (participants > 0) {
        const result = await runComplianceCheck(participants, financialSettings.cost_per_race);
        if (result && !result.compliant) {
          toast({
            title: 'Let op: Gedoogbeleid',
            description: `Met ${participants} deelnemers overschrijdt de 1e prijs €449. Bekijk het Uitbetaling tabblad voor een voorstel.`,
            variant: 'destructive',
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon instellingen niet opslaan',
        variant: 'destructive',
      });
    } finally {
      setSavingFinancial(false);
    }
  };

  // Load payout structure
  const loadPayoutStructure = async () => {
    setPayoutLoading(true);
    try {
      const data = await getPayoutStructure();
      if (data?.structure) {
        setPayoutStructure(data.structure);
      } else {
        // Load default structure if none exists
        setPayoutStructure(getDefaultPayoutStructure());
      }
    } catch (error) {
      console.error('Error loading payout structure:', error);
      setPayoutStructure(getDefaultPayoutStructure());
    } finally {
      setPayoutLoading(false);
    }
  };

  // Get default payout structure (complete structure matching Prijzen.jsx)
  const getDefaultPayoutStructure = () => ({
    '2-3': { winners: 1, payouts: [{ position: 1, percentage: 100 }] },
    '4-9': { winners: 2, payouts: [{ position: 1, percentage: 65 }, { position: 2, percentage: 35 }] },
    '10-19': { winners: 5, payouts: [{ position: 1, percentage: 40 }, { position: 2, percentage: 25 }, { position: 3, percentage: 17 }, { position: 4, percentage: 11 }, { position: 5, percentage: 7 }] },
    '20-29': { winners: 7, payouts: [{ position: 1, percentage: 38.7 }, { position: 2, percentage: 22 }, { position: 3, percentage: 13 }, { position: 4, percentage: 9 }, { position: 5, percentage: 7 }, { position: 6, percentage: 5.5 }, { position: 7, percentage: 4.8 }] },
    '30-39': { winners: 10, payouts: [{ position: 1, percentage: 34 }, { position: 2, percentage: 21.2 }, { position: 3, percentage: 12.5 }, { position: 4, percentage: 7.5 }, { position: 5, percentage: 5.6 }, { position: 6, percentage: 4.5 }, { position: 7, percentage: 4.2 }, { position: 8, percentage: 3.8 }, { position: 9, percentage: 3.5 }, { position: 10, percentage: 3.2 }] },
    '40-49': { winners: 13, payouts: [{ position: 1, percentage: 32 }, { position: 2, percentage: 20.5 }, { position: 3, percentage: 11.7 }, { position: 4, percentage: 6.8 }, { position: 5, percentage: 5.1 }, { position: 6, percentage: 3.5 }, { position: 7, percentage: 3 }, { position: 8, percentage: 3 }, { position: 9, percentage: 3 }, { position: 10, percentage: 2.6 }, { position: 11, percentage: 2.6 }, { position: 12, percentage: 2.6 }, { position: 13, percentage: 3.6 }] },
    '50-59': { winners: 16, payouts: [{ position: 1, percentage: 30 }, { position: 2, percentage: 19.6 }, { position: 3, percentage: 11.7 }, { position: 4, percentage: 6.8 }, { position: 5, percentage: 4.9 }, { position: 6, percentage: 3.7 }, { position: 7, percentage: 3.2 }, { position: 8, percentage: 2.4 }, { position: 9, percentage: 2.4 }, { position: 10, percentage: 2.4 }, { position: 11, percentage: 2.2 }, { position: 12, percentage: 2.2 }, { position: 13, percentage: 2.2 }, { position: 14, percentage: 1.9 }, { position: 15, percentage: 1.9 }, { position: 16, percentage: 2.5 }] },
    '60-69': { winners: 19, payouts: [{ position: 1, percentage: 28.6 }, { position: 2, percentage: 19 }, { position: 3, percentage: 11 }, { position: 4, percentage: 6.2 }, { position: 5, percentage: 4.8 }, { position: 6, percentage: 3.6 }, { position: 7, percentage: 3.2 }, { position: 8, percentage: 2.3 }, { position: 9, percentage: 2.3 }, { position: 10, percentage: 2.3 }, { position: 11, percentage: 2.1 }, { position: 12, percentage: 2.1 }, { position: 13, percentage: 2.1 }, { position: 14, percentage: 1.75 }, { position: 15, percentage: 1.75 }, { position: 16, percentage: 1.75 }, { position: 17, percentage: 1.75 }, { position: 18, percentage: 1.75 }, { position: 19, percentage: 1.65 }] },
    '70-79': { winners: 22, payouts: [{ position: 1, percentage: 25 }, { position: 2, percentage: 16.6 }, { position: 3, percentage: 9.6 }, { position: 4, percentage: 6.8 }, { position: 5, percentage: 5.5 }, { position: 6, percentage: 4.7 }, { position: 7, percentage: 3.5 }, { position: 8, percentage: 3.5 }, { position: 9, percentage: 3.5 }, { position: 10, percentage: 2.3 }, { position: 11, percentage: 2.3 }, { position: 12, percentage: 2.1 }, { position: 13, percentage: 2 }, { position: 14, percentage: 1.9 }, { position: 15, percentage: 1.75 }, { position: 16, percentage: 1.75 }, { position: 17, percentage: 1.75 }, { position: 18, percentage: 1.75 }, { position: 19, percentage: 1.6 }, { position: 20, percentage: 1.6 }, { position: 21, percentage: 1.6 }, { position: 22, percentage: 0.85 }] },
    '80-99': { winners: 28, payouts: [{ position: 1, percentage: 22.2 }, { position: 2, percentage: 14.7 }, { position: 3, percentage: 8.5 }, { position: 4, percentage: 6.1 }, { position: 5, percentage: 5 }, { position: 6, percentage: 4 }, { position: 7, percentage: 3.5 }, { position: 8, percentage: 3 }, { position: 9, percentage: 2.7 }, { position: 10, percentage: 2.3 }, { position: 11, percentage: 2.3 }, { position: 12, percentage: 2 }, { position: 13, percentage: 2 }, { position: 14, percentage: 1.9 }, { position: 15, percentage: 1.9 }, { position: 16, percentage: 1.75 }, { position: 17, percentage: 1.75 }, { position: 18, percentage: 1.75 }, { position: 19, percentage: 1.75 }, { position: 20, percentage: 1.6 }, { position: 21, percentage: 1.6 }, { position: 22, percentage: 1.6 }, { position: 23, percentage: 1.3 }, { position: 24, percentage: 1.3 }, { position: 25, percentage: 1.3 }, { position: 26, percentage: 1.3 }, { position: 27, percentage: 1.17 }, { position: 28, percentage: 0.73 }] },
    '100-149': { winners: 43, payouts: [{ position: 1, percentage: 18.1 }, { position: 2, percentage: 12 }, { position: 3, percentage: 7.1 }, { position: 4, percentage: 4.75 }, { position: 5, percentage: 4 }, { position: 6, percentage: 3.5 }, { position: 7, percentage: 3 }, { position: 8, percentage: 2.3 }, { position: 9, percentage: 2.2 }, { position: 10, percentage: 1.9 }, { position: 11, percentage: 1.9 }, { position: 12, percentage: 1.75 }, { position: 13, percentage: 1.6 }, { position: 14, percentage: 1.6 }, { position: 15, percentage: 1.5 }, { position: 16, percentage: 1.5 }, { position: 17, percentage: 1.5 }, { position: 18, percentage: 1.5 }, { position: 19, percentage: 1.4 }, { position: 20, percentage: 1.4 }, { position: 21, percentage: 1.4 }, { position: 22, percentage: 1.4 }, { position: 23, percentage: 1.3 }, { position: 24, percentage: 1.3 }, { position: 25, percentage: 1.3 }, { position: 26, percentage: 1.3 }, { position: 27, percentage: 1.17 }, { position: 28, percentage: 1.17 }, { position: 29, percentage: 1.05 }, { position: 30, percentage: 1.05 }, { position: 31, percentage: 1.05 }, { position: 32, percentage: 1.05 }, { position: 33, percentage: 0.95 }, { position: 34, percentage: 0.95 }, { position: 35, percentage: 0.95 }, { position: 36, percentage: 0.95 }, { position: 37, percentage: 0.88 }, { position: 38, percentage: 0.88 }, { position: 39, percentage: 0.88 }, { position: 40, percentage: 0.88 }, { position: 41, percentage: 0.81 }, { position: 42, percentage: 0.81 }, { position: 43, percentage: 0.81 }] },
    '150+': { winners: 54, payouts: [{ position: 1, percentage: 13.2 }, { position: 2, percentage: 8.8 }, { position: 3, percentage: 4.75 }, { position: 4, percentage: 3.75 }, { position: 5, percentage: 3 }, { position: 6, percentage: 2.5 }, { position: 7, percentage: 2.2 }, { position: 8, percentage: 2 }, { position: 9, percentage: 1.9 }, { position: 10, percentage: 1.6 }, { position: 11, percentage: 1.6 }, { position: 12, percentage: 1.5 }, { position: 13, percentage: 1.4 }, { position: 14, percentage: 1.3 }, { position: 15, percentage: 1.3 }, { position: 16, percentage: 1.3 }, { position: 17, percentage: 1.3 }, { position: 18, percentage: 1.2 }, { position: 19, percentage: 1.2 }, { position: 20, percentage: 1.2 }, { position: 21, percentage: 1.2 }, { position: 22, percentage: 1.1 }, { position: 23, percentage: 1.1 }, { position: 24, percentage: 1.1 }, { position: 25, percentage: 1.1 }, { position: 26, percentage: 1.1 }, { position: 27, percentage: 1.05 }, { position: 28, percentage: 1.05 }, { position: 29, percentage: 1.05 }, { position: 30, percentage: 1.05 }, { position: 31, percentage: 1.05 }, { position: 32, percentage: 1.05 }, { position: 33, percentage: 0.95 }, { position: 34, percentage: 0.95 }, { position: 35, percentage: 0.95 }, { position: 36, percentage: 0.95 }, { position: 37, percentage: 0.88 }, { position: 38, percentage: 0.88 }, { position: 39, percentage: 0.88 }, { position: 40, percentage: 0.88 }, { position: 41, percentage: 0.81 }, { position: 42, percentage: 0.81 }, { position: 43, percentage: 0.81 }, { position: 44, percentage: 0.81 }, { position: 45, percentage: 0.75 }, { position: 46, percentage: 0.75 }, { position: 47, percentage: 0.75 }, { position: 48, percentage: 0.75 }, { position: 49, percentage: 0.6 }, { position: 50, percentage: 0.6 }, { position: 51, percentage: 0.6 }, { position: 52, percentage: 0.6 }, { position: 53, percentage: 0.5 }, { position: 54, percentage: 0.47 }] },
  });

  // Update payout percentage
  const handlePayoutPercentageChange = (category, position, newPercentage) => {
    setPayoutStructure(prev => {
      const updated = { ...prev };
      const payouts = [...updated[category].payouts];
      const idx = payouts.findIndex(p => p.position === position);
      if (idx !== -1) {
        payouts[idx] = { ...payouts[idx], percentage: parseFloat(newPercentage) || 0 };
      }
      updated[category] = { ...updated[category], payouts };
      return updated;
    });
  };

  // Calculate total percentage for a category
  const getCategoryTotal = (category) => {
    if (!payoutStructure || !payoutStructure[category]) return 0;
    return payoutStructure[category].payouts.reduce((sum, p) => sum + (p.percentage || 0), 0);
  };

  // Save payout structure
  const handleSavePayoutStructure = async () => {
    setPayoutSaving(true);
    try {
      await updatePayoutStructure(payoutStructure);
      toast({
        title: 'Opgeslagen',
        description: 'Uitbetalingsstructuur is bijgewerkt',
      });
      setEditingPayoutCategory(null);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon structuur niet opslaan',
        variant: 'destructive',
      });
    } finally {
      setPayoutSaving(false);
    }
  };

  // Accept proposed compliant structure
  const handleAcceptProposedStructure = async () => {
    if (!complianceCheck?.proposed_structure) return;
    
    setPayoutSaving(true);
    try {
      // Get current structure and update the relevant category
      const currentStructure = payoutStructure || getDefaultPayoutStructure();
      const category = complianceCheck.category;
      
      // Update the category with proposed structure
      const updatedStructure = {
        ...currentStructure,
        [category]: complianceCheck.proposed_structure
      };
      
      await updatePayoutStructure(updatedStructure);
      setPayoutStructure(updatedStructure);
      
      toast({
        title: 'Structuur Aangepast',
        description: `Uitbetalingspercentages voor ${category} deelnemers zijn aangepast om binnen €449 te blijven.`,
      });
      
      // Re-run compliance check
      await runComplianceCheck();
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon structuur niet opslaan',
        variant: 'destructive',
      });
    } finally {
      setPayoutSaving(false);
    }
  };

  // Load payout structure when tab is selected
  useEffect(() => {
    if (activeTab === 'payout') {
      loadPayoutStructure();
      // Run compliance check - use a reasonable default if no users
      const numParticipants = totalUsers || users.length || 50;
      setTotalUsers(numParticipants);
      runComplianceCheck(numParticipants, financialSettings.cost_per_race);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, financialSettings.cost_per_race]);

  // Handle user export
  const handleExportUsers = async () => {
    try {
      const blob = await exportUsersCSV();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({
        title: 'Export gelukt',
        description: 'Gebruikerslijst is gedownload',
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: 'Kon export niet maken',
        variant: 'destructive',
      });
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleCreateSeason = async () => {
    try {
      await createSeason(newSeasonYear);
      toast({
        title: 'Seizoen aangemaakt',
        description: `Seizoen ${newSeasonYear} is aangemaakt`,
      });
      const updatedSeasons = await getAllSeasons();
      setSeasons(updatedSeasons);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon seizoen niet aanmaken',
        variant: 'destructive',
      });
    }
  };

  const handleStartSeason = async (year) => {
    if (!window.confirm(`Weet je zeker dat je seizoen ${year} wilt starten? Dit reset alle punten!`)) {
      return;
    }
    try {
      await startSeason(year);
      toast({
        title: 'Seizoen gestart',
        description: `Seizoen ${year} is nu actief. Alle punten zijn gereset.`,
      });
      const updatedSeasons = await getAllSeasons();
      setSeasons(updatedSeasons);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon seizoen niet starten',
        variant: 'destructive',
      });
    }
  };

  const handleCloseSeason = async () => {
    if (!closeSeasonYear || !worldChampion || !constructorChampion) {
      toast({
        title: 'Fout',
        description: 'Vul alle velden in',
        variant: 'destructive',
      });
      return;
    }

    if (!window.confirm(`Weet je zeker dat je seizoen ${closeSeasonYear} wilt afsluiten?`)) {
      return;
    }

    try {
      await closeSeason(parseInt(closeSeasonYear), worldChampion, constructorChampion);
      toast({
        title: 'Seizoen afgesloten',
        description: `Seizoen ${closeSeasonYear} is afgesloten en gearchiveerd`,
      });
      const updatedSeasons = await getAllSeasons();
      setSeasons(updatedSeasons);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon seizoen niet afsluiten',
        variant: 'destructive',
      });
    }
  };

  const handleResetSeason = async (year) => {
    if (!window.confirm(`⚠️ WAARSCHUWING: Weet je zeker dat je seizoen ${year} wilt resetten?\n\nDit verwijdert:\n- Alle voorspellingen\n- Alle race resultaten\n- Alle scores en punten\n- Extra toegevoegde races (boven de standaard 24)\n\nHet seizoen wordt teruggezet naar de originele 24 races.\n\nDeze actie kan niet ongedaan worden gemaakt!`)) {
      return;
    }
    
    try {
      const result = await resetSeason(year);
      toast({
        title: 'Seizoen gereset',
        description: `Seizoen ${year} is gereset. ${result.deleted?.predictions || 0} voorspellingen en ${result.deleted?.extra_races || 0} extra races verwijderd.`,
      });
      const updatedSeasons = await getAllSeasons();
      setSeasons(updatedSeasons);
      const updatedRaces = await getRaces(activeSeasonYear);
      setRaces(updatedRaces);
      // Refresh dashboard stats
      try {
        const dashboardData = await getAdminDashboardStats(activeSeasonYear);
        setDashboardStats(dashboardData);
      } catch (e) {}
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon seizoen niet resetten',
        variant: 'destructive',
      });
    }
  };

  const handleRescoreRace = async () => {
    if (!selectedRace) {
      toast({
        title: 'Fout',
        description: 'Selecteer eerst een race',
        variant: 'destructive',
      });
      return;
    }
    
    if (!window.confirm(`Wil je de race opnieuw scoren? Dit zal:\n- Oude punten terugdraaien\n- Nieuwe punten berekenen op basis van huidige resultaten`)) {
      return;
    }

    try {
      const result = await rescoreRace(selectedRace);
      toast({
        title: 'Race opnieuw gescoord',
        description: result.message || 'Punten zijn herberekend',
      });
      // Refresh races to show updated status
      const updatedRaces = await getRaces(activeSeasonYear);
      setRaces(updatedRaces);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon race niet opnieuw scoren',
        variant: 'destructive',
      });
    }
  };

  // Championship Results handlers
  const handleSaveChampionshipResults = async () => {
    setSavingChampionship(true);
    try {
      // Use the first position as the world champion (for backwards compatibility)
      const worldChampion = wdcTop5[0] || null;
      const constructorChampion = wccTop5[0] || null;
      
      await saveChampionshipResults(
        2026, 
        worldChampion, 
        constructorChampion,
        wdcTop5.filter(v => v), // Filter empty values
        wccTop5.filter(v => v)
      );
      
      // Reload championship results
      const results = await getChampionshipResults(2026);
      setChampionshipResults(results);
      setSelectedWorldChampion(worldChampion || '');
      setSelectedConstructorChampion(constructorChampion || '');
      
      toast({
        title: 'Opgeslagen',
        description: 'Kampioenschap resultaten (Top 5) zijn opgeslagen',
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon kampioenschap resultaten niet opslaan',
        variant: 'destructive',
      });
    } finally {
      setSavingChampionship(false);
    }
  };

  const handleFinalizeChampionship = async () => {
    // Validate that at least P1 is filled for WDC or WCC
    if (!wdcTop5[0] && !wccTop5[0]) {
      toast({
        title: 'Fout',
        description: 'Vul eerst minimaal de kampioen (P1) in voor WK of CC',
        variant: 'destructive',
      });
      return;
    }
    
    const confirmMsg = `Wilt u de kampioenschap resultaten FINALISEREN?

Dit zal:
• De resultaten definitief maken
• Punten toekennen aan alle gebruikers

Punten systeem (Top 3 voorspellingen):
• Exact juiste positie: 50 punten
• 1 positie verschil: 40 punten
• 2 posities verschil: 30 punten

Deze actie kan niet ongedaan gemaakt worden.`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setSavingChampionship(true);
    try {
      // First save the results with top 5
      const worldChampion = wdcTop5[0] || null;
      const constructorChampion = wccTop5[0] || null;
      
      await saveChampionshipResults(
        2026, 
        worldChampion, 
        constructorChampion,
        wdcTop5.filter(v => v),
        wccTop5.filter(v => v)
      );
      
      // Then finalize and score
      const result = await finalizeChampionshipResults(2026);
      
      // Reload championship results
      const results = await getChampionshipResults(2026);
      setChampionshipResults(results);
      setSelectedWorldChampion(worldChampion || '');
      setSelectedConstructorChampion(constructorChampion || '');
      
      toast({
        title: '🏆 Kampioenschap Gefinaliseerd!',
        description: `${result.predictions_scored} voorspellingen gescoord, ${result.total_points_awarded} punten uitgedeeld`,
      });
      
      // Refresh users to show updated points
      const updatedUsers = await getUsers();
      setUsers(updatedUsers);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon kampioenschap niet finaliseren',
        variant: 'destructive',
      });
    } finally {
      setSavingChampionship(false);
    }
  };

  const handleSendNewsletter = async () => {
    if (!selectedRace) {
      toast({
        title: 'Fout',
        description: 'Selecteer eerst een race',
        variant: 'destructive',
      });
      return;
    }
    
    const race = races.find(r => r.race_id === selectedRace);
    if (!window.confirm(`Wil je de nieuwsbrief versturen voor ${race?.name || 'deze race'}?\n\nDit stuurt een email naar alle deelnemers met:\n- Race resultaten\n- Top 10 stand\n- Grootste stijger & daler\n- Rode lantaarn`)) {
      return;
    }

    setSendingNewsletter(true);
    try {
      const result = await sendRaceNewsletter(selectedRace);
      toast({
        title: '📧 Nieuwsbrief Verstuurd!',
        description: result.message || `Email verstuurd naar ${result.emails_sent} deelnemers`,
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon nieuwsbrief niet versturen',
        variant: 'destructive',
      });
    } finally {
      setSendingNewsletter(false);
    }
  };

  const handleCreateTestSeason = async () => {
    if (!window.confirm('Wil je een test seizoen aanmaken?\n\nDit maakt een apart seizoen aan met de 3 pre-season test weekenden:\n• Barcelona Test (26-30 januari)\n• Bahrain Test 1 (11-13 februari)\n• Bahrain Test 2 (18-20 februari)\n\nHiermee kunnen gebruikers de pool uitproberen.')) {
      return;
    }

    setCreatingTestSeason(true);
    try {
      const result = await createTestSeason();
      toast({
        title: '✅ Test Seizoen Aangemaakt!',
        description: result.message,
      });
      // Refresh test season data
      const [testSeasonData, testRacesData] = await Promise.all([
        getTestSeason().catch(() => null),
        getTestRaces().catch(() => [])
      ]);
      setTestSeason(testSeasonData);
      setTestRaces(testRacesData);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon test seizoen niet aanmaken',
        variant: 'destructive',
      });
    } finally {
      setCreatingTestSeason(false);
    }
  };

  const handleDeleteTestSeason = async () => {
    if (!window.confirm('⚠️ Weet je zeker dat je het test seizoen wilt verwijderen?\n\nDit verwijdert:\n• Alle test voorspellingen\n• Alle test races\n• Alle test data\n\nDeze actie kan niet ongedaan worden gemaakt!')) {
      return;
    }

    setDeletingTestSeason(true);
    try {
      const result = await deleteTestSeason();
      toast({
        title: '🗑️ Test Seizoen Verwijderd',
        description: result.message,
      });
      setTestSeason(null);
      setTestRaces([]);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon test seizoen niet verwijderen',
        variant: 'destructive',
      });
    } finally {
      setDeletingTestSeason(false);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await updateSeasonSettings(parseInt(settingsSeasonYear), seasonSettings);
      toast({
        title: 'Instellingen opgeslagen',
        description: `Voorspellingsinstellingen voor seizoen ${settingsSeasonYear} zijn bijgewerkt`,
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon instellingen niet opslaan',
        variant: 'destructive',
      });
    }
  };

  const toggleSetting = (key) => {
    setSeasonSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleDeleteUser = async (userId, username, silent = false) => {
    if (!silent && !window.confirm(`Weet je zeker dat je gebruiker "${username}" wilt verwijderen? Dit verwijdert al hun voorspellingen!`)) {
      return;
    }

    try {
      await deleteUser(userId);
      if (!silent) {
        toast({
          title: 'Gebruiker verwijderd',
          description: `${username} is verwijderd`,
        });
      }
      const updatedUsers = await getAllUsers();
      setUsers(updatedUsers);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon gebruiker niet verwijderen',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteUserSeasonData = async (userId, username, season) => {
    if (!window.confirm(`Weet je zeker dat je seizoen ${season} data van "${username}" wilt verwijderen?`)) {
      return;
    }

    try {
      await deleteUserSeasonData(userId, season);
      toast({
        title: 'Seizoen data verwijderd',
        description: `Seizoen ${season} data van ${username} is verwijderd`,
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon data niet verwijderen',
        variant: 'destructive',
      });
    }
  };

  // Add new driver (reserve/replacement)
  const handleAddDriver = async () => {
    if (!newDriverName || !newDriverTeam || !newDriverNumber) {
      toast({
        title: 'Fout',
        description: 'Vul naam, team en nummer in',
        variant: 'destructive',
      });
      return;
    }

    try {
      await addDriver({
        name: newDriverName,
        team: newDriverTeam,
        number: parseInt(newDriverNumber),
        nationality: newDriverNationality || null,
      });
      
      toast({
        title: 'Coureur toegevoegd',
        description: `${newDriverName} is toegevoegd aan ${newDriverTeam}`,
      });
      
      // Clear form and refresh drivers
      setNewDriverName('');
      setNewDriverTeam('');
      setNewDriverNumber('');
      setNewDriverNationality('');
      
      const updatedDrivers = await getDrivers(activeSeasonYear);
      setDrivers(updatedDrivers);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon coureur niet toevoegen',
        variant: 'destructive',
      });
    }
  };

  // Delete reserve driver
  const handleDeleteDriver = async (driverId, driverName) => {
    if (!window.confirm(`Weet je zeker dat je ${driverName} wilt verwijderen?`)) {
      return;
    }

    try {
      await deleteDriver(driverId);
      toast({
        title: 'Coureur verwijderd',
        description: `${driverName} is verwijderd`,
      });
      
      const updatedDrivers = await getDrivers(activeSeasonYear);
      setDrivers(updatedDrivers);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon coureur niet verwijderen',
        variant: 'destructive',
      });
    }
  };

  // Edit driver
  const openEditDriver = (driver) => {
    setEditingDriver(driver);
    setEditDriverName(driver.name);
    setEditDriverTeam(driver.team);
    setEditDriverNumber(driver.number?.toString() || '');
    setEditDriverNationality(driver.nationality || '');
  };

  const closeEditDriver = () => {
    setEditingDriver(null);
    setEditDriverName('');
    setEditDriverTeam('');
    setEditDriverNumber('');
    setEditDriverNationality('');
  };

  const handleUpdateDriver = async () => {
    if (!editDriverName || !editDriverTeam || !editDriverNumber) {
      toast({
        title: 'Fout',
        description: 'Vul naam, team en nummer in',
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateDriver(editingDriver.driver_id, {
        name: editDriverName,
        team: editDriverTeam,
        number: parseInt(editDriverNumber),
        nationality: editDriverNationality || null,
      });
      
      toast({
        title: 'Coureur bijgewerkt',
        description: `${editDriverName} is bijgewerkt`,
      });
      
      closeEditDriver();
      const updatedDrivers = await getDrivers(activeSeasonYear);
      setDrivers(updatedDrivers);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon coureur niet bijwerken',
        variant: 'destructive',
      });
    }
  };

  // Edit team
  const openEditTeam = (team) => {
    setEditingTeam(team);
    setEditTeamName(team.name);
    setEditTeamColor(team.color || '#666666');
  };

  const closeEditTeam = () => {
    setEditingTeam(null);
    setEditTeamName('');
    setEditTeamColor('');
  };

  const handleUpdateTeam = async () => {
    if (!editTeamName) {
      toast({
        title: 'Fout',
        description: 'Vul een teamnaam in',
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateTeam(editingTeam.team_id, {
        name: editTeamName,
        color: editTeamColor,
      });
      
      toast({
        title: 'Team bijgewerkt',
        description: `${editTeamName} is bijgewerkt`,
      });
      
      closeEditTeam();
      const [updatedTeams, updatedDrivers] = await Promise.all([
        getTeams(activeSeasonYear),
        getDrivers(activeSeasonYear)
      ]);
      setTeams(updatedTeams);
      setDrivers(updatedDrivers);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon team niet bijwerken',
        variant: 'destructive',
      });
    }
  };

  // Add new team
  const handleAddTeam = async () => {
    if (!newTeamName) {
      toast({
        title: 'Fout',
        description: 'Vul een teamnaam in',
        variant: 'destructive',
      });
      return;
    }

    try {
      await addTeam({
        name: newTeamName,
        color: newTeamColor,
      });
      
      toast({
        title: 'Team toegevoegd',
        description: `${newTeamName} is toegevoegd`,
      });
      
      setNewTeamName('');
      setNewTeamColor('#666666');
      setShowAddTeam(false);
      const updatedTeams = await getTeams(activeSeasonYear);
      setTeams(updatedTeams);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon team niet toevoegen',
        variant: 'destructive',
      });
    }
  };

  // Delete team
  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Weet je zeker dat je ${teamName} wilt verwijderen?`)) {
      return;
    }

    try {
      await deleteTeam(teamId);
      toast({
        title: 'Team verwijderd',
        description: `${teamName} is verwijderd`,
      });
      
      const updatedTeams = await getTeams(activeSeasonYear);
      setTeams(updatedTeams);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon team niet verwijderen',
        variant: 'destructive',
      });
    }
  };

  // Race management handlers
  const resetRaceForm = () => {
    setRaceForm({
      name: '',
      circuit_name: '',
      location: '',
      country: '',
      date: '',
      time: '',
      fp1_date: '', fp1_time: '',
      fp2_date: '', fp2_time: '',
      fp3_date: '', fp3_time: '',
      qualifying_date: '', qualifying_time: '',
      is_sprint_weekend: false,
      sprint_qualifying_date: '', sprint_qualifying_time: '',
      sprint_date: '', sprint_time: '',
      circuit_layout_id: '',
    });
  };

  const openEditRace = (race) => {
    setEditingRace(race);
    // Find circuit layout ID from URL if it has a custom layout
    let layoutId = '';
    if (race.custom_circuit_layout) {
      const match = race.custom_circuit_layout.match(/circuit_([^.]+)/);
      if (match) layoutId = match[1];
    }
    setRaceForm({
      name: race.name || '',
      circuit_name: race.circuit_name || '',
      location: race.location || '',
      country: race.country || '',
      date: race.date || '',
      time: race.time || '',
      fp1_date: race.fp1?.date || '', fp1_time: race.fp1?.time || '',
      fp2_date: race.fp2?.date || '', fp2_time: race.fp2?.time || '',
      fp3_date: race.fp3?.date || '', fp3_time: race.fp3?.time || '',
      qualifying_date: race.qualifying?.date || '', qualifying_time: race.qualifying?.time || '',
      is_sprint_weekend: race.is_sprint_weekend || false,
      sprint_qualifying_date: race.sprint_qualifying?.date || '', sprint_qualifying_time: race.sprint_qualifying?.time || '',
      sprint_date: race.sprint?.date || '', sprint_time: race.sprint?.time || '',
      circuit_layout_id: layoutId,
    });
    setShowAddRace(false);
  };

  const closeEditRace = () => {
    setEditingRace(null);
    resetRaceForm();
  };

  const handleAddRace = async () => {
    if (!raceForm.name || !raceForm.location || !raceForm.date) {
      toast({
        title: 'Fout',
        description: 'Vul naam, locatie en datum in',
        variant: 'destructive',
      });
      return;
    }

    try {
      await createRace({
        name: raceForm.name,
        circuit_name: raceForm.circuit_name || raceForm.location,
        location: raceForm.location,
        country: raceForm.country,
        date: raceForm.date,
        time: raceForm.time || '15:00',
        fp1: { date: raceForm.fp1_date, time: raceForm.fp1_time },
        fp2: { date: raceForm.fp2_date, time: raceForm.fp2_time },
        fp3: { date: raceForm.fp3_date, time: raceForm.fp3_time },
        qualifying: { date: raceForm.qualifying_date, time: raceForm.qualifying_time },
        is_sprint_weekend: raceForm.is_sprint_weekend,
        sprint_qualifying: raceForm.is_sprint_weekend ? { date: raceForm.sprint_qualifying_date, time: raceForm.sprint_qualifying_time } : null,
        sprint: raceForm.is_sprint_weekend ? { date: raceForm.sprint_date, time: raceForm.sprint_time } : null,
      });
      
      // If circuit layout was selected, assign it to the race
      if (raceForm.circuit_layout_id) {
        const token = localStorage.getItem('token');
        const raceId = `${activeSeasonYear}_${raceForm.location.toLowerCase().replace(' ', '_')}`;
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/races/${raceId}/circuit-layout?circuit_id=${raceForm.circuit_layout_id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast({
        title: 'Race toegevoegd',
        description: `${raceForm.name} is toegevoegd`,
      });
      
      setShowAddRace(false);
      resetRaceForm();
      const updatedRaces = await getRaces(activeSeasonYear);
      setRaces(updatedRaces);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon race niet toevoegen',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateRace = async () => {
    if (!raceForm.name || !raceForm.location || !raceForm.date) {
      toast({
        title: 'Fout',
        description: 'Vul naam, locatie en datum in',
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateRace(editingRace.race_id, {
        name: raceForm.name,
        circuit_name: raceForm.circuit_name,
        location: raceForm.location,
        country: raceForm.country,
        date: raceForm.date,
        time: raceForm.time,
        fp1: { date: raceForm.fp1_date, time: raceForm.fp1_time },
        fp2: { date: raceForm.fp2_date, time: raceForm.fp2_time },
        fp3: { date: raceForm.fp3_date, time: raceForm.fp3_time },
        qualifying: { date: raceForm.qualifying_date, time: raceForm.qualifying_time },
        is_sprint_weekend: raceForm.is_sprint_weekend,
        sprint_qualifying: { date: raceForm.sprint_qualifying_date, time: raceForm.sprint_qualifying_time },
        sprint: { date: raceForm.sprint_date, time: raceForm.sprint_time },
      });
      
      // Update circuit layout if changed
      const token = localStorage.getItem('token');
      if (raceForm.circuit_layout_id) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/races/${editingRace.race_id}/circuit-layout?circuit_id=${raceForm.circuit_layout_id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Remove custom layout if none selected
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/races/${editingRace.race_id}/circuit-layout`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast({
        title: 'Race bijgewerkt',
        description: `${raceForm.name} is bijgewerkt`,
      });
      
      closeEditRace();
      const updatedRaces = await getRaces(activeSeasonYear);
      setRaces(updatedRaces);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon race niet bijwerken',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteRace = async (raceId, raceName) => {
    const confirmMessage = `⚠️ WAARSCHUWING: Weet je zeker dat je "${raceName}" wilt verwijderen?\n\nDit verwijdert ook:\n- Alle voorspellingen van gebruikers voor deze race\n- Alle ingevoerde resultaten\n- Alle scores voor deze race\n\nDeze actie kan niet ongedaan worden gemaakt!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const result = await deleteRace(raceId);
      toast({
        title: 'Race verwijderd',
        description: `${raceName} is verwijderd${result.deleted_predictions > 0 ? ` (inclusief ${result.deleted_predictions} voorspellingen)` : ''}`,
      });
      
      closeEditRace();
      const updatedRaces = await getRaces(activeSeasonYear);
      setRaces(updatedRaces);
      
      // Refresh dashboard stats
      try {
        const dashboardData = await getAdminDashboardStats(activeSeasonYear);
        setDashboardStats(dashboardData);
      } catch (e) {}
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon race niet verwijderen',
        variant: 'destructive',
      });
    }
  };

  // Toggle user admin role
  const handleToggleAdminRole = async (userId, username, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const action = newRole === 'admin' ? 'admin maken' : 'admin rechten verwijderen';
    
    if (!window.confirm(`Weet je zeker dat je ${username} wilt ${action}?`)) {
      return;
    }

    try {
      await updateUserRole(userId, newRole);
      toast({
        title: 'Rol gewijzigd',
        description: `${username} is nu ${newRole === 'admin' ? 'admin' : 'gebruiker'}`,
      });
      
      const updatedUsers = await getAllUsers();
      setUsers(updatedUsers);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon rol niet wijzigen',
        variant: 'destructive',
      });
    }
  };

  // Toggle user activation status
  const handleToggleUserActivation = async (userId, username, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'activeren' : 'deactiveren';
    
    if (!window.confirm(`Weet je zeker dat je ${username} wilt ${action}?${!newStatus ? '\n\nDeze gebruiker kan dan geen voorspellingen meer doen.' : ''}`)) {
      return;
    }

    try {
      await toggleUserActivation(userId, newStatus);
      toast({
        title: newStatus ? 'Gebruiker Geactiveerd' : 'Gebruiker Gedeactiveerd',
        description: `${username} is nu ${newStatus ? 'geactiveerd en kan voorspellingen doen' : 'gedeactiveerd en kan geen voorspellingen meer doen'}`,
      });
      
      const updatedUsers = await getAllUsers();
      setUsers(updatedUsers);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon activatiestatus niet wijzigen',
        variant: 'destructive',
      });
    }
  };

  // Admin submit prediction for other user
  const handleAdminSubmitPrediction = async () => {
    if (!adminPredUser || !adminPredRace) {
      toast({
        title: 'Fout',
        description: 'Selecteer een gebruiker en een race',
        variant: 'destructive',
      });
      return;
    }

    // Validate top 5 - no duplicates
    const filledTop5 = adminTop5.filter(d => d !== '');
    if (filledTop5.length > 0 && new Set(filledTop5).size !== filledTop5.length) {
      toast({
        title: 'Fout',
        description: 'Elke coureur kan maar één keer in de top 5 staan',
        variant: 'destructive',
      });
      return;
    }

    // Validate qualifying - no duplicates
    const filledQual = adminQualifying.filter(d => d !== '');
    if (filledQual.length > 0 && new Set(filledQual).size !== filledQual.length) {
      toast({
        title: 'Fout',
        description: 'Elke coureur kan maar één keer in de kwalificatie staan',
        variant: 'destructive',
      });
      return;
    }

    // Validate pole time format if provided
    if (adminPoleTime) {
      const poleTimeRegex = /^[0-5][0-9]:[0-5][0-9]\.[0-9]{3}$/;
      if (!poleTimeRegex.test(adminPoleTime)) {
        toast({
          title: 'Ongeldige Pole Tijd',
          description: 'Pole tijd moet in het formaat MM:SS.mmm zijn (bijv. 01:30.456)',
          variant: 'destructive',
        });
        return;
      }
    }

    try {
      const targetUser = users.find(u => u.id === adminPredUser);
      
      // Submit race prediction if top 5 is filled
      if (filledTop5.length === 5) {
        await adminCreateRacePrediction(
          adminPredUser,
          adminPredRace,
          adminTop5[0], // race winner
          adminTop5,
          adminFastestLap || null,
          adminSafetyCar || null,
          adminVSC || null,
          adminDnfCount || 0,
          adminRainRace || null,
          adminSprintWinner || null,
          adminSprintPoleWinner || null,
          adminSprintPoleTime || null
        );
      }

      // Submit qualifying prediction if filled
      if (filledQual.length === 3) {
        await adminCreateQualifyingPrediction(
          adminPredUser,
          adminPredRace,
          adminQualifying,
          adminPoleTime || null
        );
      }

      toast({
        title: 'Voorspelling opgeslagen',
        description: `Voorspelling voor ${targetUser?.username || 'gebruiker'} is opgeslagen`,
      });

      // Clear form
      setAdminTop5(['', '', '', '', '']);
      setAdminQualifying(['', '', '']);
      setAdminFastestLap('');
      setAdminPoleTime('');
      setAdminSafetyCar('');
      setAdminVSC('');
      setAdminDnfCount('');
      setAdminRainRace('');
      setAdminSprintWinner('');
      setAdminSprintPoleWinner('');
      setAdminSprintPoleTime('');
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon voorspelling niet opslaan',
        variant: 'destructive',
      });
    }
  };

  // Admin submit championship prediction for user
  const handleAdminSubmitChampionship = async () => {
    if (!adminChampUser) {
      toast({
        title: 'Fout',
        description: 'Selecteer eerst een gebruiker',
        variant: 'destructive',
      });
      return;
    }

    const wcFilled = adminChampWC.filter(Boolean);
    const ccFilled = adminChampCC.filter(Boolean);

    if (wcFilled.length === 0 && ccFilled.length === 0) {
      toast({
        title: 'Fout',
        description: 'Vul minimaal één voorspelling in',
        variant: 'destructive',
      });
      return;
    }

    try {
      const targetUser = users.find(u => u.id === adminChampUser);
      
      // Use admin endpoint to save championship prediction for user
      await adminCreateChampionshipPrediction(
        adminChampUser,
        2026,
        wcFilled.length > 0 ? adminChampWC : null,
        ccFilled.length > 0 ? adminChampCC : null
      );

      toast({
        title: 'Kampioenschap voorspelling opgeslagen',
        description: `Kampioenschap voorspelling voor ${targetUser?.username || 'gebruiker'} is opgeslagen`,
      });

      // Clear form
      setAdminChampWC(['', '', '']);
      setAdminChampCC(['', '', '']);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon kampioenschap voorspelling niet opslaan',
        variant: 'destructive',
      });
    }
  };

  const handleEnterResults = async () => {
    // Determine if this is a test race and get settings
    const isTestRace = testRaces?.some(r => r.race_id === selectedRace);
    let settings = {};
    
    if (isTestRace && testSeason?.prediction_settings) {
      settings = testSeason.prediction_settings;
    } else {
      // Use regular season settings
      settings = seasonSettings || {};
    }
    
    // Default to true if not specified
    const raceTop5Enabled = settings.race_top5_enabled !== false;
    const qualifyingTop3Enabled = settings.qualifying_top3_enabled !== false;
    
    // Check filled positions
    const filledPositions = racePositions.filter(p => p !== '').length;
    const qualTop3Filled = qualTop3.filter(d => d && d !== '').length;
    
    // Validate based on settings
    if (!selectedRace) {
      toast({
        title: 'Fout',
        description: 'Selecteer eerst een race',
        variant: 'destructive',
      });
      return;
    }
    
    if (raceTop5Enabled && filledPositions < 10) {
      toast({
        title: 'Fout',
        description: 'Vul minimaal de top 10 race posities in',
        variant: 'destructive',
      });
      return;
    }
    
    // Qualifying validation depends on both qualifying_top3_enabled AND qualifying_p2p3_enabled
    const qualP2P3Enabled = settings.qualifying_p2p3_enabled === true;
    if (qualifyingTop3Enabled) {
      if (qualP2P3Enabled && qualTop3Filled < 3) {
        toast({
          title: 'Fout',
          description: 'Vul de kwalificatie top 3 in',
          variant: 'destructive',
        });
        return;
      } else if (!qualP2P3Enabled && qualTop3Filled < 1) {
        toast({
          title: 'Fout',
          description: 'Vul de kwalificatie Pole Position (P1) in',
          variant: 'destructive',
        });
        return;
      }
    }

    try {
      const resultsData = {
        race_positions: racePositions.filter(p => p !== ''), // All filled positions
        qualifying_top3: qualifyingTop3Enabled ? qualTop3.filter(d => d && d !== '') : [],
        fastest_lap: fastestLap || null,
        pole_time: poleTime || null,
        safety_car: safetyCar || null,
        virtual_safety_car: virtualSafetyCar || null,
        dnf_count: dnfCount ? parseInt(dnfCount) : null,
        rain_race: rainRace || null,
        dnf_drivers: dnfDrivers,
      };
      
      // Add sprint results if applicable
      const currentRace = [...races, ...(testRaces || [])].find(r => r.race_id === selectedRace);
      if (currentRace?.is_sprint_weekend) {
        resultsData.sprint_winner = sprintWinner || null;
        resultsData.sprint_pole_winner = sprintPoleWinner || null;
        resultsData.sprint_pole_time = sprintPoleTime || null;
      }
      
      const saveResponse = await enterRaceResults(selectedRace, resultsData);
      
      // Check if backend automatically rescored the race
      if (saveResponse.rescored) {
        toast({
          title: '✅ Resultaten opgeslagen & punten herberekend',
          description: saveResponse.message,
        });
      } else {
        toast({
          title: 'Resultaten opgeslagen',
          description: 'De race resultaten zijn opgeslagen',
        });
        
        // Automatically score the race after saving results (only if not already rescored)
        try {
          const scoreResult = await scoreRace(selectedRace);
          toast({
            title: 'Punten berekend',
            description: `${scoreResult.predictions_scored || 0} voorspellingen gescoord voor ${scoreResult.users_scored || 0} gebruikers`,
          });
        } catch (scoreError) {
          console.log('Scoring skipped or failed:', scoreError.response?.data?.detail);
          // Don't show error - scoring may fail if no predictions exist yet
        }
      }
      
      // Refresh races to show updated status
      if (isTestRace) {
        const updatedTestRaces = await getTestRaces();
        setTestRaces(updatedTestRaces);
      } else {
        const updatedRaces = await getRaces(activeSeasonYear);
        setRaces(updatedRaces);
      }
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon resultaten niet opslaan',
        variant: 'destructive',
      });
    }
  };

  const handleScoreRace = async () => {
    if (!selectedRace) {
      toast({
        title: 'Fout',
        description: 'Selecteer eerst een race',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await scoreRace(selectedRace);
      toast({
        title: 'Race gescoord',
        description: `${result.predictions_scored || 0} voorspellingen gescoord voor ${result.users_scored || 0} gebruikers`,
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon race niet scoren',
        variant: 'destructive',
      });
    }
  };

  // Toggle DNF status for a driver
  const toggleDnf = (driverId) => {
    setDnfDrivers(prev => 
      prev.includes(driverId) 
        ? prev.filter(d => d !== driverId)
        : [...prev, driverId]
    );
  };

  // Load existing race results when a race is selected
  const loadExistingResults = async (raceId) => {
    try {
      // Find the race in either regular races or test races
      const race = [...races, ...(testRaces || [])].find(r => r.race_id === raceId);
      console.log('Loading results for race:', raceId, 'Found race:', race?.name, 'Has results:', !!race?.results);
      
      if (race && race.results) {
        const results = race.results;
        console.log('Results object:', results);
        
        // Load race positions
        if (results.race_positions && results.race_positions.length > 0) {
          const positions = Array(22).fill('');
          results.race_positions.forEach((driverId, index) => {
            if (index < 22) positions[index] = driverId;
          });
          setRacePositions(positions);
          console.log('Loaded race positions:', results.race_positions.length);
        } else {
          // Clear if no positions
          setRacePositions(Array(22).fill(''));
        }
        
        // Load qualifying top 3
        if (results.qualifying_top3 && results.qualifying_top3.length > 0) {
          const qual = ['', '', ''];
          results.qualifying_top3.forEach((driverId, index) => {
            if (index < 3) qual[index] = driverId;
          });
          setQualTop3(qual);
          console.log('Loaded qualifying:', results.qualifying_top3);
        } else {
          setQualTop3(['', '', '']);
        }
        
        // Load other fields
        setFastestLap(results.fastest_lap || '');
        setPoleTime(results.pole_time || '');
        setSafetyCar(results.safety_car || '');
        setVirtualSafetyCar(results.virtual_safety_car || '');
        setDnfCount(results.dnf_count?.toString() || '');
        setRainRace(results.rain_race || '');
        setDnfDrivers(results.dnf_drivers || []);
        
        // Sprint results
        if (results.sprint_winner) setSprintWinner(results.sprint_winner);
        if (results.sprint_pole_winner) setSprintPoleWinner(results.sprint_pole_winner);
        if (results.sprint_pole_time) setSprintPoleTime(results.sprint_pole_time);
        
        toast({
          title: 'Resultaten geladen',
          description: `Bestaande resultaten voor ${race.name} zijn geladen`,
        });
      } else {
        // Clear form if no results
        console.log('No results found, clearing form');
        clearResults();
      }
    } catch (error) {
      console.error('Error loading results:', error);
    }
  };

  // Clear all race results
  const clearResults = () => {
    setRacePositions(Array(22).fill(''));
    setQualTop3(['', '', '']);
    setFastestLap('');
    setPoleTime('');
    setSafetyCar('');
    setVirtualSafetyCar('');
    setDnfCount('');
    setRainRace('');
    setDnfDrivers([]);
    setSprintWinner('');
    setSprintPoleTime('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Laden...</div>
      </div>
    );
  }

  const settingsOptions = [
    { key: 'race_top5_enabled', label: 'Race Top 5', description: 'Voorspel de top 5 finishers van de race' },
    { key: 'qualifying_top3_enabled', label: 'Kwalificatie Pole (P1)', description: 'Voorspel de pole positie winnaar' },
    { key: 'qualifying_p2p3_enabled', label: 'Kwalificatie P2 & P3', description: 'Voorspel ook P2 en P3 van de kwalificatie' },
    { key: 'pole_time_enabled', label: 'Pole Tijd', description: 'Voorspel de pole position tijd' },
    { key: 'fastest_lap_enabled', label: 'Snelste Ronde', description: 'Voorspel wie de snelste ronde rijdt' },
    { key: 'safety_car_enabled', label: 'Safety Car', description: 'Voorspel of er een Safety Car komt' },
    { key: 'virtual_safety_car_enabled', label: 'Virtual Safety Car', description: 'Voorspel of er een Virtual Safety Car komt' },
    { key: 'dnf_count_enabled', label: 'Aantal DNF', description: 'Voorspel het aantal uitvallers' },
    { key: 'rain_race_enabled', label: 'Regen Race', description: 'Voorspel of het een regen race wordt' },
    { key: 'world_champion_enabled', label: 'Wereldkampioen', description: 'Voorspel de wereldkampioen' },
    { key: 'constructor_champion_enabled', label: 'Constructeurs Kampioen', description: 'Voorspel de constructeurs kampioen' },
    { key: 'sprint_race_enabled', label: 'Sprint Race Winnaar', description: 'Voorspel de winnaar van sprint races (10 punten)' },
    { key: 'sprint_pole_winner_enabled', label: 'Sprint Kwalificatie Winnaar', description: 'Voorspel wie pole pakt in sprint kwalificatie (10 punten)' },
    { key: 'sprint_qualifying_enabled', label: 'Sprint Pole Tijd', description: 'Voorspel sprint pole tijd (exact 40 pts, binnen 0.5 sec 10 pts)' },
  ];

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-block bg-[#FDB913] rounded-full p-4 mb-4">
            <Settings size={40} className="text-[#1a2332]" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-2">Admin Beheer</h1>
          <p className="text-muted-foreground">Beheer seizoenen, gebruikers, instellingen en race resultaten</p>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value); setMobileMenuOpen(false); }} className="w-full">
          {/* Mobile/Tablet Hamburger Menu - visible below lg breakpoint (1024px) */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-[#FDB913] text-[#1a2332] rounded-lg font-semibold"
            >
              <span className="flex items-center">
                <Menu size={20} className="mr-2" />
                {activeTab === 'seasons' && 'Seizoenen'}
                {activeTab === 'races' && 'Races'}
                {activeTab === 'drivers' && 'Coureurs'}
                {activeTab === 'predictions' && 'Voorspellingen'}
                {activeTab === 'users' && 'Gebruikers'}
                {activeTab === 'results' && 'Resultaten'}
                {activeTab === 'settings' && 'Instellingen'}
                {activeTab === 'circuits' && 'Circuit Layouts'}
                {activeTab === 'payout' && 'Uitbetaling'}
                {activeTab === 'test-season' && 'Test Seizoen'}
                {activeTab === 'help' && 'Help'}
              </span>
              <ChevronDown size={20} className={`transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {mobileMenuOpen && (
              <div className="mt-2 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
                <button onClick={() => { setActiveTab('dashboard'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'dashboard' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <LayoutDashboard className="mr-2" size={16} /> Dashboard
                </button>
                <button onClick={() => { setActiveTab('seasons'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'seasons' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <Calendar className="mr-2" size={16} /> Seizoenen
                </button>
                <button onClick={() => { setActiveTab('races'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'races' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <Flag className="mr-2" size={16} /> Races
                </button>
                <button onClick={() => { setActiveTab('drivers'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'drivers' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <Car className="mr-2" size={16} /> Coureurs
                </button>
                <button onClick={() => { setActiveTab('predictions'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'predictions' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <ClipboardList className="mr-2" size={16} /> Voorspellingen
                </button>
                <button onClick={() => { setActiveTab('users'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'users' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <Users className="mr-2" size={16} /> Gebruikers
                </button>
                <button onClick={() => { setActiveTab('results'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'results' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <Trophy className="mr-2" size={16} /> Resultaten
                </button>
                <button onClick={() => { setActiveTab('settings'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'settings' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <ToggleLeft className="mr-2" size={16} /> Instellingen
                </button>
                <button onClick={() => { setActiveTab('circuits'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'circuits' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <Image className="mr-2" size={16} /> Circuit Layouts
                </button>
                <button onClick={() => { setActiveTab('payout'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'payout' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <Percent className="mr-2" size={16} /> Uitbetaling
                </button>
                <button onClick={() => { setActiveTab('test-season'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'test-season' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <TestTube2 className="mr-2" size={16} /> Test Seizoen
                </button>
                <button onClick={() => { setActiveTab('help'); setMobileMenuOpen(false); }} className={`w-full flex items-center px-4 py-3 text-left ${activeTab === 'help' ? 'bg-[#FDB913] text-[#1a2332]' : 'hover:bg-secondary text-foreground'}`}>
                  <HelpCircle className="mr-2" size={16} /> Help
                </button>
              </div>
            )}
          </div>

          {/* Desktop Tabs - hidden below lg breakpoint (1024px) */}
          <div className="hidden lg:block overflow-x-auto pb-2 mb-6">
            <TabsList className="inline-flex min-w-max gap-1 p-1 bg-secondary rounded-lg">
              <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <LayoutDashboard className="mr-1.5" size={14} />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="seasons" data-testid="admin-tab-seasons" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <Calendar className="mr-1.5" size={14} />
                Seizoenen
              </TabsTrigger>
              <TabsTrigger value="races" data-testid="admin-tab-races" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <Flag className="mr-1.5" size={14} />
                Races
              </TabsTrigger>
              <TabsTrigger value="drivers" data-testid="admin-tab-drivers" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <Car className="mr-1.5" size={14} />
                Coureurs
              </TabsTrigger>
              <TabsTrigger value="predictions" data-testid="admin-tab-predictions" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <ClipboardList className="mr-1.5" size={14} />
                Voorspellingen
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <Users className="mr-1.5" size={14} />
                Gebruikers
              </TabsTrigger>
              <TabsTrigger value="results" data-testid="admin-tab-results" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <Trophy className="mr-1.5" size={14} />
                Resultaten
              </TabsTrigger>
              <TabsTrigger value="settings" data-testid="admin-tab-settings" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <ToggleLeft className="mr-1.5" size={14} />
                Instellingen
              </TabsTrigger>
              <TabsTrigger value="circuits" data-testid="admin-tab-circuits" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <Image className="mr-1.5" size={14} />
                Circuits
              </TabsTrigger>
              <TabsTrigger value="payout" data-testid="admin-tab-payout" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <Percent className="mr-1.5" size={14} />
                Uitbetaling
              </TabsTrigger>
              <TabsTrigger value="test-season" data-testid="admin-tab-test-season" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <TestTube2 className="mr-1.5" size={14} />
                Test Seizoen
              </TabsTrigger>
              <TabsTrigger value="help" data-testid="admin-tab-help" className="px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332]">
                <HelpCircle className="mr-1.5" size={14} />
                Help
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="space-y-6">
              {/* Stats Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-2 border-[#FDB913]">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Races met resultaten</p>
                        <p className="text-3xl font-bold text-green-500">{dashboardStats.races_with_results || 0}</p>
                      </div>
                      <CheckCircle className="h-10 w-10 text-green-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-orange-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Races zonder resultaten</p>
                        <p className="text-3xl font-bold text-orange-500">{dashboardStats.races_without_results || 0}</p>
                      </div>
                      <AlertCircle className="h-10 w-10 text-orange-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-blue-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Totaal voorspellingen</p>
                        <p className="text-3xl font-bold text-blue-500">{dashboardStats.total_predictions || 0}</p>
                      </div>
                      <ClipboardList className="h-10 w-10 text-blue-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-purple-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Actieve gebruikers</p>
                        <p className="text-3xl font-bold text-purple-500">{dashboardStats.active_users || 0}</p>
                      </div>
                      <Users className="h-10 w-10 text-purple-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Races Overview */}
              <Card className="border-2 border-[#FDB913]">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Flag className="mr-2 text-[#FDB913]" size={20} />
                    Races Overzicht - Seizoen 2026
                  </CardTitle>
                  <CardDescription>
                    Overzicht van alle races, resultaten status en aantal voorspellingen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-3 px-2 font-semibold text-muted-foreground">Ronde</th>
                          <th className="text-left py-3 px-2 font-semibold text-muted-foreground">Race</th>
                          <th className="text-left py-3 px-2 font-semibold text-muted-foreground">Datum</th>
                          <th className="text-center py-3 px-2 font-semibold text-muted-foreground">Resultaten</th>
                          <th className="text-center py-3 px-2 font-semibold text-muted-foreground">Voorspellingen</th>
                          <th className="text-center py-3 px-2 font-semibold text-muted-foreground">Actie</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardStats.races?.map((race) => (
                          <tr key={race.race_id} className="border-b border-gray-800 hover:bg-gray-800/50">
                            <td className="py-3 px-2 font-bold text-[#FDB913]">{race.round}</td>
                            <td className="py-3 px-2">
                              <div className="flex items-center space-x-2">
                                {getRaceFlagIcon(race) ? (
                                  <img src={getRaceFlagIcon(race)} alt="" className="w-5 h-4 rounded-sm object-cover" />
                                ) : (
                                  <span>🏁</span>
                                )}
                                <span className="text-foreground">{race.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-muted-foreground">{race.date}</td>
                            <td className="py-3 px-2 text-center">
                              {race.has_results ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                                  <CheckCircle size={12} className="mr-1" />
                                  Ingevuld
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400">
                                  <AlertCircle size={12} className="mr-1" />
                                  Open
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-2 text-center">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                                {dashboardStats.predictions_per_race?.[race.race_id] || 0}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-center">
                              {!race.has_results && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs border-[#FDB913] text-[#FDB913] hover:bg-[#FDB913] hover:text-[#1a2332]"
                                  onClick={() => {
                                    setActiveTab('results');
                                    setSelectedRace(race.race_id);
                                  }}
                                >
                                  <Trophy size={12} className="mr-1" />
                                  Invoeren
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Seasons Tab */}
          <TabsContent value="seasons">
            <SeasonsTab 
              seasons={seasons}
              refreshSeasons={refreshSeasons}
              toast={toast}
            />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="border-2 border-[#FDB913]">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ToggleLeft className="mr-2 text-[#FDB913]" size={20} />
                  Voorspellingsinstellingen per Seizoen
                </CardTitle>
                <CardDescription>
                  Schakel voorspellingsopties in of uit per seizoen. Uitgeschakelde opties zijn niet zichtbaar voor gebruikers.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Season Selection */}
                <div>
                  <Label className="font-semibold">Selecteer Seizoen</Label>
                  <Select value={settingsSeasonYear} onValueChange={setSettingsSeasonYear}>
                    <SelectTrigger className="w-full md:w-64">
                      <SelectValue placeholder="Selecteer seizoen" />
                    </SelectTrigger>
                    <SelectContent>
                      {seasons.map((season) => (
                        <SelectItem key={season.year} value={season.year.toString()}>
                          {season.year} {season.is_active && '(Actief)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Settings Toggles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {settingsOptions.map((option) => (
                    <div 
                      key={option.key} 
                      className={`flex items-center justify-between p-4 border rounded-lg ${
                        seasonSettings[option.key] ? 'bg-green-500/20 dark:bg-green-900/30 border-green-500 dark:border-green-600' : 'bg-secondary border-border'
                      }`}
                    >
                      <div className="flex-1">
                        <Label className="font-semibold">{option.label}</Label>
                        <p className="text-sm text-muted-foreground">{option.description}</p>
                      </div>
                      <Switch
                        checked={seasonSettings[option.key]}
                        onCheckedChange={() => toggleSetting(option.key)}
                        className="ml-4"
                      />
                    </div>
                  ))}
                </div>

                {/* Save Button */}
                <Button 
                  onClick={handleUpdateSettings}
                  className="w-full bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]"
                >
                  <CheckCircle className="mr-2" size={16} />
                  Voorspellingsinstellingen Opslaan
                </Button>
              </CardContent>
            </Card>

            {/* Financial Settings Card */}
            <Card className="border-2 border-green-500 mt-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Euro className="mr-2 text-green-600" size={20} />
                  Financiële Instellingen
                </CardTitle>
                <CardDescription>
                  Beheer het inleggeld, administratiekosten en betaallink voor het seizoen.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-semibold">Inleggeld per Race (€)</Label>
                    <Input
                      type="number"
                      step="0.50"
                      min="0"
                      value={financialSettings.cost_per_race}
                      onChange={(e) => setFinancialSettings(prev => ({ ...prev, cost_per_race: parseFloat(e.target.value) || 0 }))}
                      className="mt-1"
                      placeholder="1.00"
                    />
                    <p className="text-sm text-muted-foreground mt-1">Dit bedrag x aantal races = inschrijfgeld basis</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Administratiekosten (€)</Label>
                    <Input
                      type="number"
                      step="0.50"
                      min="0"
                      value={financialSettings.admin_cost}
                      onChange={(e) => setFinancialSettings(prev => ({ ...prev, admin_cost: parseFloat(e.target.value) || 0 }))}
                      className="mt-1"
                      placeholder="1.00"
                    />
                    <p className="text-sm text-muted-foreground mt-1">Eenmalige kosten bovenop inleggeld</p>
                  </div>
                  <div>
                    <Label className="font-semibold flex items-center">
                      <Star className="mr-2 text-purple-500" size={16} />
                      Jaarbonus (€)
                    </Label>
                    <Input
                      type="number"
                      step="0.50"
                      min="0"
                      value={financialSettings.yearly_bonus}
                      onChange={(e) => setFinancialSettings(prev => ({ ...prev, yearly_bonus: parseFloat(e.target.value) || 0 }))}
                      className="mt-1"
                      placeholder="0.00"
                    />
                    <p className="text-sm text-muted-foreground mt-1">Extra inleg per deelnemer. Winnaar: hoogste score in één race</p>
                  </div>
                </div>

                <div>
                  <Label className="font-semibold flex items-center">
                    <Link className="mr-2" size={16} />
                    Betaallink
                  </Label>
                  <Input
                    type="url"
                    value={financialSettings.payment_link}
                    onChange={(e) => setFinancialSettings(prev => ({ ...prev, payment_link: e.target.value }))}
                    className="mt-1"
                    placeholder="https://tikkie.me/..."
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Deze link wordt meegestuurd in de welkomst-email naar nieuwe gebruikers
                  </p>
                </div>

                {/* Preview of entry fee calculation */}
                <div className="bg-green-500/20 dark:bg-green-900/40 border border-green-500/50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 dark:text-green-400 mb-2">Berekening Inschrijfgeld</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Inleggeld per race:</span>
                      <span>€{financialSettings.cost_per_race?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Aantal races (2026):</span>
                      <span>24</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Administratiekosten:</span>
                      <span>€{financialSettings.admin_cost?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between text-purple-700 dark:text-purple-400">
                      <span>Jaarbonus:</span>
                      <span>€{financialSettings.yearly_bonus?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between font-bold text-green-800 dark:text-green-400 border-t border-green-300 dark:border-green-700 pt-2 mt-2">
                      <span>Totaal per deelnemer:</span>
                      <span>€{((financialSettings.cost_per_race * 24) + financialSettings.admin_cost + (financialSettings.yearly_bonus || 0)).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleUpdateFinancialSettings}
                  disabled={savingFinancial}
                  className="w-full bg-green-600 text-white hover:bg-green-700"
                >
                  <CheckCircle className="mr-2" size={16} />
                  {savingFinancial ? 'Opslaan...' : 'Financiële Instellingen Opslaan'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Circuit Layouts Tab */}
          <TabsContent value="circuits">
            <CircuitLayoutManager />
          </TabsContent>

          {/* Payout Structure Tab */}
          <TabsContent value="payout">
            <PayoutTab 
              users={users}
              totalUsers={totalUsers}
              setTotalUsers={setTotalUsers}
              financialSettings={financialSettings}
              payoutStructure={payoutStructure}
              setPayoutStructure={setPayoutStructure}
              payoutLoading={payoutLoading}
              loadPayoutStructure={loadPayoutStructure}
              payoutSaving={payoutSaving}
              setPayoutSaving={setPayoutSaving}
              complianceCheck={complianceCheck}
              checkingCompliance={checkingCompliance}
              runComplianceCheck={runComplianceCheck}
              toast={toast}
            />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UsersTab 
              users={users}
              refreshUsers={refreshUsers}
              user={user}
              toast={toast}
            />
          </TabsContent>

          {/* Races Tab */}
          <TabsContent value="races">
            <Card className="border-2 border-[#FDB913]">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Flag className="mr-2 text-[#FDB913]" size={20} />
                    Race Kalender {activeSeasonYear} ({races.length} races)
                  </div>
                  <Button
                    onClick={() => { setShowAddRace(true); setEditingRace(null); resetRaceForm(); }}
                    className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]"
                  >
                    <Plus className="mr-1" size={14} />
                    Race Toevoegen
                  </Button>
                </CardTitle>
                <CardDescription>
                  Beheer races, locaties, tijden en sprint weekenden
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {races.sort((a, b) => new Date(a.date) - new Date(b.date)).map((race) => (
                    <div 
                      key={race.race_id}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-secondary cursor-pointer group"
                      onClick={() => openEditRace(race)}
                    >
                      <div className="flex items-center space-x-4">
                        <span className="font-mono text-sm text-muted-foreground w-8">R{race.round}</span>
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">{race.name}</span>
                            {race.is_sprint_weekend && (
                              <span className="text-xs bg-purple-500/20 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded">
                                Sprint
                              </span>
                            )}
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              race.status === 'completed' ? 'bg-green-500/20 dark:bg-green-900/50 text-green-600 dark:text-green-400' :
                              race.status === 'in_progress' ? 'bg-yellow-500/20 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400' :
                              'bg-secondary text-muted-foreground'
                            }`}>
                              {race.status === 'completed' ? 'Voltooid' : 
                               race.status === 'in_progress' ? 'Bezig' : 'Gepland'}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {race.location}, {race.country} • {race.date} {race.time} UTC ({formatLocalTime(race.date, race.time)} NL)
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); openEditRace(race); }}
                        className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Add/Edit Race Modal */}
            {(showAddRace || editingRace) && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>{editingRace ? 'Race Bewerken' : 'Nieuwe Race Toevoegen'}</span>
                      <Button variant="ghost" size="sm" onClick={() => { setShowAddRace(false); closeEditRace(); }}>
                        <X size={18} />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Race Naam *</Label>
                        <Input
                          value={raceForm.name}
                          onChange={(e) => setRaceForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Bijv. Dutch Grand Prix"
                        />
                      </div>
                      <div>
                        <Label>Circuit Naam</Label>
                        <Input
                          value={raceForm.circuit_name}
                          onChange={(e) => setRaceForm(prev => ({ ...prev, circuit_name: e.target.value }))}
                          placeholder="Bijv. Circuit Zandvoort"
                        />
                      </div>
                      <div>
                        <Label>Locatie *</Label>
                        <Input
                          value={raceForm.location}
                          onChange={(e) => setRaceForm(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="Bijv. Zandvoort"
                        />
                      </div>
                      <div>
                        <Label>Land</Label>
                        <Input
                          value={raceForm.country}
                          onChange={(e) => setRaceForm(prev => ({ ...prev, country: e.target.value }))}
                          placeholder="Bijv. Netherlands"
                        />
                      </div>
                      <div>
                        <Label>Circuit Layout</Label>
                        <Select
                          value={raceForm.circuit_layout_id || "none"}
                          onValueChange={(value) => setRaceForm(prev => ({ ...prev, circuit_layout_id: value === "none" ? "" : value }))}
                        >
                          <SelectTrigger className="bg-white dark:bg-slate-700">
                            <SelectValue placeholder="Selecteer layout (optioneel)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Geen (automatisch)</SelectItem>
                            {circuitLayouts.map((layout) => (
                              <SelectItem key={layout.id} value={layout.id}>
                                {layout.original_filename} ({layout.width}x{layout.height})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Upload nieuwe layouts via de "Circuits" tab
                        </p>
                      </div>
                    </div>

                    {/* Race Date/Time */}
                    <div className="p-4 bg-[#FDB913]/20 dark:bg-[#FDB913]/10 rounded-lg border border-[#FDB913]/50">
                      <h4 className="font-semibold mb-3 flex items-center">
                        <Trophy className="mr-2 text-[#FDB913]" size={16} />
                        Race
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Datum *</Label>
                          <Input
                            type="date"
                            value={raceForm.date}
                            onChange={(e) => setRaceForm(prev => ({ ...prev, date: e.target.value }))}
                            className="bg-white dark:bg-slate-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <Label>Tijd (lokaal)</Label>
                          <Input
                            type="time"
                            value={raceForm.time}
                            onChange={(e) => setRaceForm(prev => ({ ...prev, time: e.target.value }))}
                            className="bg-white dark:bg-slate-700 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Sprint Weekend Toggle */}
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_sprint"
                        checked={raceForm.is_sprint_weekend}
                        onChange={(e) => setRaceForm(prev => ({ ...prev, is_sprint_weekend: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <Label htmlFor="is_sprint" className="font-medium text-purple-700">
                        Dit is een Sprint Weekend
                      </Label>
                    </div>

                    {/* Sessions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* FP1 */}
                      <div className="p-3 bg-secondary rounded-lg border">
                        <Label className="font-semibold text-sm">Vrije Training 1</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <Input
                            type="date"
                            value={raceForm.fp1_date}
                            onChange={(e) => setRaceForm(prev => ({ ...prev, fp1_date: e.target.value }))}
                            className="text-sm bg-white dark:bg-slate-700 dark:text-white"
                          />
                          <Input
                            type="time"
                            value={raceForm.fp1_time}
                            onChange={(e) => setRaceForm(prev => ({ ...prev, fp1_time: e.target.value }))}
                            className="text-sm bg-white dark:bg-slate-700 dark:text-white"
                          />
                        </div>
                      </div>

                      {/* FP2 (only for non-sprint weekends) */}
                      {!raceForm.is_sprint_weekend && (
                        <div className="p-3 bg-secondary rounded-lg border">
                          <Label className="font-semibold text-sm">Vrije Training 2</Label>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <Input
                              type="date"
                              value={raceForm.fp2_date}
                              onChange={(e) => setRaceForm(prev => ({ ...prev, fp2_date: e.target.value }))}
                              className="text-sm bg-white dark:bg-slate-700 dark:text-white"
                            />
                            <Input
                              type="time"
                              value={raceForm.fp2_time}
                              onChange={(e) => setRaceForm(prev => ({ ...prev, fp2_time: e.target.value }))}
                              className="text-sm bg-white dark:bg-slate-700 dark:text-white"
                            />
                          </div>
                        </div>
                      )}

                      {/* FP3 (only for non-sprint weekends) */}
                      {!raceForm.is_sprint_weekend && (
                        <div className="p-3 bg-secondary rounded-lg border">
                          <Label className="font-semibold text-sm">Vrije Training 3</Label>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <Input
                              type="date"
                              value={raceForm.fp3_date}
                              onChange={(e) => setRaceForm(prev => ({ ...prev, fp3_date: e.target.value }))}
                              className="text-sm bg-white dark:bg-slate-700 dark:text-white"
                            />
                            <Input
                              type="time"
                              value={raceForm.fp3_time}
                              onChange={(e) => setRaceForm(prev => ({ ...prev, fp3_time: e.target.value }))}
                              className="text-sm bg-white dark:bg-slate-700 dark:text-white"
                            />
                          </div>
                        </div>
                      )}

                      {/* Sprint Qualifying (only for sprint weekends) */}
                      {raceForm.is_sprint_weekend && (
                        <div className="p-3 bg-purple-500/20 dark:bg-purple-900/40 rounded-lg border border-purple-500/50">
                          <Label className="font-semibold text-sm text-purple-700 dark:text-purple-300">Sprint Kwalificatie</Label>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <Input
                              type="date"
                              value={raceForm.sprint_qualifying_date}
                              onChange={(e) => setRaceForm(prev => ({ ...prev, sprint_qualifying_date: e.target.value }))}
                              className="text-sm bg-white dark:bg-slate-700 dark:text-white border-purple-300 dark:border-purple-600"
                            />
                            <Input
                              type="time"
                              value={raceForm.sprint_qualifying_time}
                              onChange={(e) => setRaceForm(prev => ({ ...prev, sprint_qualifying_time: e.target.value }))}
                              className="text-sm bg-white dark:bg-slate-700 dark:text-white border-purple-300 dark:border-purple-600"
                            />
                          </div>
                        </div>
                      )}

                      {/* Sprint Race (only for sprint weekends) */}
                      {raceForm.is_sprint_weekend && (
                        <div className="p-3 bg-green-500/20 dark:bg-green-900/40 rounded-lg border border-green-500/50">
                          <Label className="font-semibold text-sm text-green-700 dark:text-green-300">Sprint Race</Label>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <Input
                              type="date"
                              value={raceForm.sprint_date}
                              onChange={(e) => setRaceForm(prev => ({ ...prev, sprint_date: e.target.value }))}
                              className="text-sm bg-white dark:bg-slate-700 dark:text-white border-green-300 dark:border-green-600"
                            />
                            <Input
                              type="time"
                              value={raceForm.sprint_time}
                              onChange={(e) => setRaceForm(prev => ({ ...prev, sprint_time: e.target.value }))}
                              className="text-sm bg-white dark:bg-slate-700 dark:text-white border-green-300 dark:border-green-600"
                            />
                          </div>
                        </div>
                      )}

                      {/* Qualifying */}
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                        <Label className="font-semibold text-sm text-blue-700 dark:text-blue-300">Kwalificatie</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <Input
                            type="date"
                            value={raceForm.qualifying_date}
                            onChange={(e) => setRaceForm(prev => ({ ...prev, qualifying_date: e.target.value }))}
                            className="text-sm bg-white dark:bg-slate-700 dark:text-white border-blue-300 dark:border-blue-600"
                          />
                          <Input
                            type="time"
                            value={raceForm.qualifying_time}
                            onChange={(e) => setRaceForm(prev => ({ ...prev, qualifying_time: e.target.value }))}
                            className="text-sm bg-white dark:bg-slate-700 dark:text-white border-blue-300 dark:border-blue-600"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2 pt-4 border-t">
                      <Button 
                        onClick={editingRace ? handleUpdateRace : handleAddRace}
                        className="flex-1 bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]"
                      >
                        <CheckCircle className="mr-2" size={16} />
                        {editingRace ? 'Opslaan' : 'Race Toevoegen'}
                      </Button>
                      {editingRace && (
                        <Button 
                          variant="outline" 
                          onClick={() => handleDeleteRace(editingRace.race_id, editingRace.name)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Drivers Tab */}
          <TabsContent value="drivers">
            <div className="space-y-6">
              {/* Teams/Constructors Section */}
              <Card className="border-2 border-blue-400">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Flag className="mr-2 text-blue-500" size={20} />
                      Constructeurs ({teams.length})
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddTeam(!showAddTeam)}
                      className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                      <Plus className="mr-1" size={14} />
                      Team Toevoegen
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Add Team Form */}
                  {showAddTeam && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Teamnaam *</Label>
                          <Input
                            placeholder="Bijv. Andretti"
                            value={newTeamName}
                            onChange={(e) => setNewTeamName(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Kleur</Label>
                          <div className="flex items-center space-x-2">
                            <Input
                              type="color"
                              value={newTeamColor}
                              onChange={(e) => setNewTeamColor(e.target.value)}
                              className="w-16 h-9 p-1 cursor-pointer"
                            />
                            <Input
                              value={newTeamColor}
                              onChange={(e) => setNewTeamColor(e.target.value)}
                              placeholder="#666666"
                              className="flex-1"
                            />
                          </div>
                        </div>
                        <div className="flex items-end space-x-2">
                          <Button onClick={handleAddTeam} className="bg-blue-600 hover:bg-blue-700">
                            <Plus className="mr-1" size={14} />
                            Toevoegen
                          </Button>
                          <Button variant="outline" onClick={() => setShowAddTeam(false)}>
                            Annuleren
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Teams List */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {teams.map((team) => (
                      <div 
                        key={team.team_id}
                        className="flex items-center justify-between p-3 rounded-lg border hover:shadow-md transition-shadow"
                        style={{ borderLeft: `4px solid ${team.color}` }}
                      >
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-6 h-6 rounded-full"
                            style={{ backgroundColor: team.color }}
                          />
                          <span className="font-medium">{team.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditTeam(team)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        >
                          <Edit size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Edit Team Modal */}
              {editingTeam && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <Card className="w-full max-w-md m-4">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>Team Bewerken</span>
                        <Button variant="ghost" size="sm" onClick={closeEditTeam}>
                          <X size={18} />
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Teamnaam *</Label>
                        <Input
                          value={editTeamName}
                          onChange={(e) => setEditTeamName(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Kleur</Label>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="color"
                            value={editTeamColor}
                            onChange={(e) => setEditTeamColor(e.target.value)}
                            className="w-16 h-9 p-1 cursor-pointer"
                          />
                          <Input
                            value={editTeamColor}
                            onChange={(e) => setEditTeamColor(e.target.value)}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div className="flex space-x-2 pt-4">
                        <Button onClick={handleUpdateTeam} className="flex-1 bg-blue-600 hover:bg-blue-700">
                          <CheckCircle className="mr-2" size={16} />
                          Opslaan
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={() => handleDeleteTeam(editingTeam.team_id, editingTeam.name)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Add Driver Form */}
                <Card className="border-2 border-[#FDB913]">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <UserPlus className="mr-2 text-[#FDB913]" size={20} />
                      Reserve Coureur Toevoegen
                    </CardTitle>
                    <CardDescription>
                      Voeg een reserve/vervangend coureur toe (bijv. bij ziekte)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Naam *</Label>
                      <Input
                        placeholder="Volledige naam (bijv. Nyck de Vries)"
                        value={newDriverName}
                        onChange={(e) => setNewDriverName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Team *</Label>
                      <Select value={newDriverTeam} onValueChange={setNewDriverTeam}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer team" />
                        </SelectTrigger>
                        <SelectContent>
                          {teams.map((team) => (
                            <SelectItem key={team.team_id} value={team.name}>
                              {team.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Startnummer *</Label>
                        <Input
                          type="number"
                          placeholder="Nummer"
                          value={newDriverNumber}
                          onChange={(e) => setNewDriverNumber(e.target.value)}
                          min={1}
                          max={99}
                        />
                      </div>
                      <div>
                        <Label>Nationaliteit</Label>
                        <Input
                          placeholder="Bijv. Netherlands"
                          value={newDriverNationality}
                          onChange={(e) => setNewDriverNationality(e.target.value)}
                        />
                      </div>
                    </div>
                    <Button 
                      onClick={handleAddDriver}
                      className="w-full bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]"
                    >
                      <Plus className="mr-2" size={16} />
                      Coureur Toevoegen
                    </Button>
                  </CardContent>
                </Card>

                {/* Current Drivers List */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Car className="mr-2" size={20} />
                      Huidige Coureurs ({drivers.length})
                    </CardTitle>
                    <CardDescription>
                      Klik op een coureur om te bewerken
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {teams.map((team) => (
                        <div key={team.team_id} className="mb-4">
                          <div 
                            className="font-semibold text-sm mb-2 px-2 py-1 rounded"
                            style={{ backgroundColor: team.color + '20', borderLeft: `4px solid ${team.color}` }}
                          >
                            {team.name}
                          </div>
                          <div className="space-y-1 ml-2">
                            {drivers
                              .filter(d => d.team === team.name)
                              .map((driver) => (
                                <div 
                                  key={driver.driver_id} 
                                  className="flex items-center justify-between p-2 rounded hover:bg-secondary cursor-pointer group"
                                  onClick={() => openEditDriver(driver)}
                                >
                                  <div className="flex items-center space-x-2">
                                    <span className="font-mono text-sm text-muted-foreground w-8">#{driver.number}</span>
                                    <span>{formatDriverName(driver.name)}</span>
                                    {driver.is_reserve && (
                                      <span className="text-xs bg-orange-500/20 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded">
                                        Reserve
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); openEditDriver(driver); }}
                                      className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                    >
                                      <Edit size={14} />
                                    </Button>
                                    {driver.is_reserve && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteDriver(driver.driver_id, driver.name); }}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 size={14} />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Edit Driver Modal */}
              {editingDriver && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <Card className="w-full max-w-md m-4">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>Coureur Bewerken</span>
                        <Button variant="ghost" size="sm" onClick={closeEditDriver}>
                          <X size={18} />
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Naam *</Label>
                        <Input
                          value={editDriverName}
                          onChange={(e) => setEditDriverName(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Team *</Label>
                        <Select value={editDriverTeam} onValueChange={setEditDriverTeam}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecteer team" />
                          </SelectTrigger>
                          <SelectContent>
                            {teams.map((team) => (
                              <SelectItem key={team.team_id} value={team.name}>
                                {team.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Startnummer *</Label>
                          <Input
                            type="number"
                            value={editDriverNumber}
                            onChange={(e) => setEditDriverNumber(e.target.value)}
                            min={1}
                            max={99}
                          />
                        </div>
                        <div>
                          <Label>Nationaliteit</Label>
                          <Input
                            value={editDriverNationality}
                            onChange={(e) => setEditDriverNationality(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="flex space-x-2 pt-4">
                        <Button onClick={handleUpdateDriver} className="flex-1 bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]">
                          <CheckCircle className="mr-2" size={16} />
                          Opslaan
                        </Button>
                        {editingDriver.is_reserve && (
                          <Button 
                            variant="outline" 
                            onClick={() => { handleDeleteDriver(editingDriver.driver_id, editingDriver.name); closeEditDriver(); }}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Predictions Tab - Admin submit for other users */}
          <TabsContent value="predictions">
            {/* Export to Excel Section */}
            <Card className="border-2 border-green-400 mb-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileDown className="mr-2 text-green-500" size={20} />
                  Export naar Excel
                </CardTitle>
                <CardDescription>
                  Download alle voorspellingen van alle gebruikers voor alle races als Excel bestand.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={async () => {
                    try {
                      toast({ title: 'Bezig met exporteren...', description: 'Even geduld...' });
                      await exportPredictionsToExcel();
                      toast({ 
                        title: '✅ Export succesvol!', 
                        description: 'Het Excel bestand wordt gedownload.' 
                      });
                    } catch (error) {
                      toast({ 
                        title: 'Fout', 
                        description: 'Kon voorspellingen niet exporteren: ' + (error.response?.data?.detail || error.message),
                        variant: 'destructive'
                      });
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <FileDown className="mr-2" size={16} />
                  Download Excel Export
                </Button>
                <p className="text-sm text-muted-foreground mt-2">
                  Het bestand bevat een overzichtstab, een tab per race met alle voorspellingen, en een tab voor kampioenschap voorspellingen.
                </p>
                <Button 
                  onClick={async () => {
                    try {
                      toast({ title: 'Bezig met samenvoegen...', description: 'Even geduld...' });
                      const result = await mergeDuplicatePredictions();
                      toast({ 
                        title: result.merged > 0 ? 'Samenvoegen succesvol!' : 'Geen duplicaten',
                        description: result.message
                      });
                      if (viewPredictionsRace) {
                        loadPredictionsForRace(viewPredictionsRace);
                      }
                    } catch (error) {
                      toast({ 
                        title: 'Fout', 
                        description: 'Kon duplicaten niet samenvoegen: ' + (error.response?.data?.detail || error.message),
                        variant: 'destructive'
                      });
                    }
                  }}
                  variant="outline"
                  className="mt-3 border-orange-500 text-orange-600 hover:bg-orange-50"
                  data-testid="merge-duplicates-btn"
                >
                  <RefreshCcw className="mr-2" size={16} />
                  Dubbele Voorspellingen Samenvoegen
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  Voegt dubbele race/sprint voorspellingen per gebruiker samen tot één document.
                </p>

                <Button 
                  onClick={async () => {
                    try {
                      toast({ title: 'Bezig met synchroniseren...', description: 'Racetijden worden opgehaald van OpenF1 API...' });
                      const result = await syncRaceTimes();
                      toast({ 
                        title: 'Synchronisatie succesvol!',
                        description: result.message
                      });
                    } catch (error) {
                      toast({ 
                        title: 'Fout', 
                        description: 'Synchronisatie mislukt: ' + (error.response?.data?.detail || error.message),
                        variant: 'destructive'
                      });
                    }
                  }}
                  variant="outline"
                  className="mt-3 border-blue-500 text-blue-600 hover:bg-blue-50"
                  data-testid="sync-race-times-btn"
                >
                  <RefreshCcw className="mr-2" size={16} />
                  Racetijden Synchroniseren (OpenF1)
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  Haalt de laatste racetijden op van de OpenF1 API. Automatisch elke maandag om 03:00.
                </p>

                <Button 
                  onClick={async () => {
                    try {
                      toast({ title: 'Bezig met herberekenen...', description: 'Alle punten worden opnieuw berekend...' });
                      const result = await recalculateLeaderboard();
                      toast({ 
                        title: 'Herberekening succesvol!',
                        description: result.message
                      });
                    } catch (error) {
                      toast({ 
                        title: 'Fout', 
                        description: 'Herberekening mislukt: ' + (error.response?.data?.detail || error.message),
                        variant: 'destructive'
                      });
                    }
                  }}
                  variant="outline"
                  className="mt-3 border-red-500 text-red-600 hover:bg-red-50"
                  data-testid="recalculate-leaderboard-btn"
                >
                  <RefreshCcw className="mr-2" size={16} />
                  Klassement Herberekenen
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  Voegt dubbele voorspellingen samen en herberekent alle punten van scratch. Gebruik na correcties.
                </p>
              </CardContent>
            </Card>

            <PointsAuditCard />

            <SprintRecoveryCard />

            {/* View Predictions Section */}
            <Card className="border-2 border-blue-400 mb-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ClipboardList className="mr-2 text-blue-500" size={20} />
                  Voorspellingen Bekijken per Race
                </CardTitle>
                <CardDescription>
                  Bekijk alle ingevoerde voorspellingen van alle gebruikers (incl. admins) per race.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Label className="font-semibold">Selecteer Race</Label>
                  <Select 
                    value={viewPredictionsRace} 
                    onValueChange={(val) => {
                      setViewPredictionsRace(val);
                      loadPredictionsForRace(val);
                    }}
                  >
                    <SelectTrigger className="max-w-md">
                      <SelectValue placeholder="Selecteer een race om voorspellingen te bekijken" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[400px]">
                      {races.map((race) => {
                        const flagUrl = getRaceFlagIcon(race);
                        return (
                          <SelectItem key={race.race_id} value={race.race_id}>
                            <span className="flex items-center">
                              {flagUrl ? (
                                <img src={flagUrl} alt="" className="inline-block w-5 h-4 mr-2 rounded-sm object-cover" />
                              ) : (
                                <span className="mr-2">🏁</span>
                              )}
                              {race.name} ({race.status})
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {loadingPredictions && (
                  <div className="text-center py-4 text-muted-foreground">Laden...</div>
                )}

                {!loadingPredictions && viewPredictionsRace && racePredictions.length === 0 && (
                  <div className="text-center py-4 text-muted-foreground bg-secondary rounded-lg">
                    Nog geen voorspellingen voor deze race.
                  </div>
                )}

                {!loadingPredictions && racePredictions.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-secondary">
                          <th className="border p-2 text-left font-semibold">Gebruiker</th>
                          <th className="border p-2 text-left font-semibold">Kwalificatie P1-P3</th>
                          <th className="border p-2 text-left font-semibold">Pole Tijd</th>
                          <th className="border p-2 text-left font-semibold">Race Top 5</th>
                          <th className="border p-2 text-left font-semibold">Snelste Ronde</th>
                          <th className="border p-2 text-center font-semibold">SC</th>
                          <th className="border p-2 text-center font-semibold">VSC</th>
                          <th className="border p-2 text-center font-semibold">DNF</th>
                          <th className="border p-2 text-center font-semibold">Regen</th>
                          {races.find(r => r.race_id === viewPredictionsRace)?.is_sprint_weekend && (
                            <>
                              <th className="border p-2 text-left font-semibold bg-purple-500/10 text-purple-600 dark:text-purple-400">Sprint Winnaar</th>
                              <th className="border p-2 text-left font-semibold bg-purple-500/10 text-purple-600 dark:text-purple-400">Sprint Pole</th>
                              <th className="border p-2 text-left font-semibold bg-purple-500/10 text-purple-600 dark:text-purple-400">Sprint Tijd</th>
                            </>
                          )}
                          <th className="border p-2 text-center font-semibold">Actie</th>
                        </tr>
                      </thead>
                      <tbody>
                        {racePredictions.map((pred, idx) => (
                          <tr key={pred.user_id} className={idx % 2 === 0 ? 'bg-card' : 'bg-secondary'}>
                            <td className="border p-2 font-medium">{pred.username}</td>
                            <td className="border p-2 text-xs">
                              {pred.qualifying_prediction?.qualifying?.length > 0 
                                ? pred.qualifying_prediction.qualifying.map((d, i) => (
                                    <div key={i}><span className="text-blue-600 font-medium">P{i+1}:</span> {d}</div>
                                  ))
                                : <span className="text-gray-400">-</span>
                              }
                            </td>
                            <td className="border p-2 font-mono text-xs">
                              {pred.qualifying_prediction?.pole_time || '-'}
                            </td>
                            <td className="border p-2 text-xs">
                              {pred.race_prediction?.top5?.length > 0 
                                ? pred.race_prediction.top5.map((d, i) => (
                                    <div key={i}><span className="text-[#FDB913] font-medium">P{i+1}:</span> {d}</div>
                                  ))
                                : <span className="text-gray-400">-</span>
                              }
                            </td>
                            <td className="border p-2 text-xs">{pred.race_prediction?.fastest_lap || '-'}</td>
                            <td className="border p-2 text-center">
                              {pred.race_prediction?.safety_car === true || pred.race_prediction?.safety_car === 'ja' 
                                ? <span className="text-green-600 font-bold">Ja</span> 
                                : pred.race_prediction?.safety_car === false || pred.race_prediction?.safety_car === 'nee' 
                                  ? <span className="text-red-600 font-bold">Nee</span> 
                                  : '-'}
                            </td>
                            <td className="border p-2 text-center">
                              {pred.race_prediction?.virtual_safety_car === true || pred.race_prediction?.virtual_safety_car === 'ja' 
                                ? <span className="text-green-600 font-bold">Ja</span> 
                                : pred.race_prediction?.virtual_safety_car === false || pred.race_prediction?.virtual_safety_car === 'nee' 
                                  ? <span className="text-red-600 font-bold">Nee</span> 
                                  : '-'}
                            </td>
                            <td className="border p-2 text-center font-medium">
                              {pred.race_prediction?.dnf_count ?? '-'}
                            </td>
                            <td className="border p-2 text-center">
                              {pred.race_prediction?.rain_race === true || pred.race_prediction?.rain_race === 'ja' 
                                ? <span className="text-green-600 font-bold">Ja</span> 
                                : pred.race_prediction?.rain_race === false || pred.race_prediction?.rain_race === 'nee' 
                                  ? <span className="text-red-600 font-bold">Nee</span> 
                                  : '-'}
                            </td>
                            {races.find(r => r.race_id === viewPredictionsRace)?.is_sprint_weekend && (
                              <>
                                <td className="border p-2 text-xs bg-purple-500/5">
                                  {pred.race_prediction?.sprint_winner || <span className="text-red-400 font-medium">Ontbreekt</span>}
                                </td>
                                <td className="border p-2 text-xs bg-purple-500/5">
                                  {pred.race_prediction?.sprint_pole_winner || <span className="text-red-400 font-medium">Ontbreekt</span>}
                                </td>
                                <td className="border p-2 font-mono text-xs bg-purple-500/5">
                                  {pred.race_prediction?.sprint_pole_time || <span className="text-red-400 font-medium">Ontbreekt</span>}
                                </td>
                              </>
                            )}
                            <td className="border p-2 text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditModal(pred)}
                                className="hover:bg-blue-50"
                              >
                                <Edit size={14} className="mr-1" />
                                Bewerken
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-2 text-sm text-muted-foreground">
                      Totaal: {racePredictions.length} voorspelling(en)
                    </div>
                  </div>
                )}

                {/* Edit Modal */}
                {editingPrediction && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                      <div className="sticky top-0 bg-card border-b p-4 flex justify-between items-center">
                        <h3 className="text-lg font-bold text-foreground">
                          Voorspelling Bewerken: {editingPrediction.username}
                        </h3>
                        <button onClick={closeEditModal} className="text-muted-foreground hover:text-foreground">
                          <X size={24} />
                        </button>
                      </div>
                      
                      <div className="p-6 space-y-6">
                        {/* Qualifying Section */}
                        <div className="border border-border rounded-lg p-4 bg-secondary/30">
                          <h4 className="font-bold text-blue-500 dark:text-blue-400 mb-4">Kwalificatie Voorspelling</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {[0, 1, 2].map((pos) => (
                              <div key={pos}>
                                <Label className="text-sm font-bold text-blue-500 dark:text-blue-400">P{pos + 1}</Label>
                                <Select
                                  value={editQualifying[pos]}
                                  onValueChange={(val) => {
                                    const newQual = [...editQualifying];
                                    newQual[pos] = val;
                                    setEditQualifying(newQual);
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecteer coureur" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {drivers.map((d) => (
                                      <SelectItem key={d.driver_id} value={d.driver_id}>
                                        {d.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                          <div className="max-w-xs">
                            <Label className="text-sm font-bold text-blue-500 dark:text-blue-400">Pole Tijd</Label>
                            <Input
                              value={editPoleTime}
                              onChange={(e) => setEditPoleTime(e.target.value)}
                              placeholder="MM:SS.mmm"
                            />
                          </div>
                        </div>

                        {/* Race Section */}
                        <div className="border border-border rounded-lg p-4 bg-secondary/30">
                          <h4 className="font-bold text-[#FDB913] mb-4">Race Voorspelling</h4>
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                            {[0, 1, 2, 3, 4].map((pos) => (
                              <div key={pos}>
                                <Label className="text-sm font-bold text-[#FDB913]">P{pos + 1}</Label>
                                <Select
                                  value={editTop5[pos]}
                                  onValueChange={(val) => {
                                    const newTop5 = [...editTop5];
                                    newTop5[pos] = val;
                                    setEditTop5(newTop5);
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecteer" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {drivers.map((d) => (
                                      <SelectItem key={d.driver_id} value={d.driver_id}>
                                        {d.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            <div>
                              <Label className="text-sm font-bold">Snelste Ronde</Label>
                              <Select value={editFastestLap} onValueChange={setEditFastestLap}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecteer" />
                                </SelectTrigger>
                                <SelectContent>
                                  {drivers.map((d) => (
                                    <SelectItem key={d.driver_id} value={d.driver_id}>
                                      {d.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm font-bold">Safety Car</Label>
                              <Select value={editSafetyCar} onValueChange={setEditSafetyCar}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Ja/Nee" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ja">Ja</SelectItem>
                                  <SelectItem value="nee">Nee</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm font-bold">Virtual SC</Label>
                              <Select value={editVSC} onValueChange={setEditVSC}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Ja/Nee" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ja">Ja</SelectItem>
                                  <SelectItem value="nee">Nee</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm font-bold">Aantal DNF</Label>
                              <Select value={editDnfCount} onValueChange={setEditDnfCount}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Aantal" />
                                </SelectTrigger>
                                <SelectContent>
                                  {[0,1,2,3,4,5,6,7,8,9,10].map((n) => (
                                    <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm font-bold">Regen Race</Label>
                              <Select value={editRainRace} onValueChange={setEditRainRace}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Ja/Nee" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ja">Ja</SelectItem>
                                  <SelectItem value="nee">Nee</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        {/* Sprint Weekend Section */}
                        {viewPredictionsRace && races.find(r => r.race_id === viewPredictionsRace)?.is_sprint_weekend && (
                          <div className="border border-purple-500 rounded-lg p-4 bg-purple-500/10" data-testid="edit-sprint-section">
                            <h4 className="font-bold text-purple-400 mb-4 flex items-center">
                              <Zap className="mr-2" size={16} />
                              Sprint Weekend Voorspellingen
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label className="text-sm font-bold text-purple-400">Sprint Race Winnaar</Label>
                                <Select value={editSprintWinner} onValueChange={setEditSprintWinner}>
                                  <SelectTrigger data-testid="edit-sprint-winner">
                                    <SelectValue placeholder="Selecteer coureur" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {drivers.map((d) => (
                                      <SelectItem key={d.driver_id} value={d.driver_id}>
                                        {d.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-sm font-bold text-purple-400">Sprint Kwalificatie Winnaar</Label>
                                <Select value={editSprintPoleWinner} onValueChange={setEditSprintPoleWinner}>
                                  <SelectTrigger data-testid="edit-sprint-pole-winner">
                                    <SelectValue placeholder="Selecteer coureur" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {drivers.map((d) => (
                                      <SelectItem key={d.driver_id} value={d.driver_id}>
                                        {d.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-sm font-bold text-purple-400">Sprint Pole Tijd</Label>
                                <Input
                                  value={editSprintPoleTime}
                                  onChange={(e) => setEditSprintPoleTime(e.target.value)}
                                  placeholder="MM:SS.mmm"
                                  data-testid="edit-sprint-pole-time"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="sticky bottom-0 bg-card border-t p-4 flex justify-end gap-3">
                        <Button variant="outline" onClick={closeEditModal}>
                          Annuleren
                        </Button>
                        <Button 
                          onClick={handleSaveEdit}
                          disabled={savingEdit}
                          className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]"
                        >
                          {savingEdit ? 'Opslaan...' : 'Opslaan'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Existing: Submit Prediction for User */}
            <Card className="border-2 border-[#FDB913]">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ClipboardList className="mr-2 text-[#FDB913]" size={20} />
                  Voorspelling Invoeren voor Gebruiker
                </CardTitle>
                <CardDescription>
                  Voer een voorspelling in namens een andere gebruiker (bijv. bij ziekte of vergeten).
                  Als admin kun je ook na de deadline voorspellingen invoeren.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* User and Race Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-semibold">Selecteer Gebruiker *</Label>
                    <Select value={adminPredUser} onValueChange={setAdminPredUser}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer gebruiker" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => u.role !== 'admin').map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.username} ({u.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-semibold">Selecteer Race *</Label>
                    <Select value={adminPredRace} onValueChange={setAdminPredRace}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer race" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[400px]">
                        {races.map((race) => {
                          const flagUrl = getRaceFlagIcon(race);
                          return (
                            <SelectItem key={race.race_id} value={race.race_id}>
                              <span className="flex items-center">
                                {flagUrl ? (
                                  <img src={flagUrl} alt="" className="inline-block w-5 h-4 mr-2 rounded-sm object-cover" />
                                ) : (
                                  <span className="mr-2">🏁</span>
                                )}
                                {race.name} ({race.status})
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Qualifying Prediction */}
                <div className="bg-[#1a2332] p-4 rounded-lg border-2 border-blue-500/50">
                  <Label className="font-semibold text-lg mb-3 block text-white">Kwalificatie Top 3</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {[1, 2, 3].map((pos) => (
                      <div key={pos}>
                        <Label className="text-sm font-bold text-blue-400">P{pos}</Label>
                        <Select 
                          value={adminQualifying[pos - 1]} 
                          onValueChange={(val) => {
                            const newQual = [...adminQualifying];
                            newQual[pos - 1] = val;
                            setAdminQualifying(newQual);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`P${pos}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((driver) => (
                              <SelectItem 
                                key={driver.driver_id} 
                                value={driver.driver_id}
                                disabled={adminQualifying.includes(driver.driver_id) && adminQualifying[pos - 1] !== driver.driver_id}
                              >
                                {formatDriverNameLastFirst(driver)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <div>
                    <Label className="text-white">Pole Tijd (MM:SS.mmm)</Label>
                    <Input
                      placeholder="01:30.456"
                      value={adminPoleTime}
                      onChange={(e) => setAdminPoleTime(e.target.value)}
                      className="max-w-xs"
                    />
                  </div>
                </div>

                {/* Race Prediction */}
                <div className="bg-[#1a2332] p-4 rounded-lg border-2 border-[#FDB913]">
                  <Label className="font-semibold text-lg mb-3 block text-[#FDB913]">Race Top 5</Label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    {[1, 2, 3, 4, 5].map((pos) => (
                      <div key={pos}>
                        <Label className="text-sm font-bold text-[#FDB913]">P{pos}</Label>
                        <Select 
                          value={adminTop5[pos - 1]} 
                          onValueChange={(val) => {
                            const newTop5 = [...adminTop5];
                            newTop5[pos - 1] = val;
                            setAdminTop5(newTop5);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`P${pos}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((driver) => (
                              <SelectItem 
                                key={driver.driver_id} 
                                value={driver.driver_id}
                                disabled={adminTop5.includes(driver.driver_id) && adminTop5[pos - 1] !== driver.driver_id}
                              >
                                {formatDriverNameLastFirst(driver)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Extra Predictions */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div>
                    <Label>Snelste Ronde</Label>
                    <Select value={adminFastestLap} onValueChange={setAdminFastestLap}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer" />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.map((driver) => (
                          <SelectItem key={driver.driver_id} value={driver.driver_id}>
                            {formatDriverName(driver.name).split(' ').pop()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Safety Car</Label>
                    <Select value={adminSafetyCar} onValueChange={setAdminSafetyCar}>
                      <SelectTrigger>
                        <SelectValue placeholder="Ja/Nee" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nee">Nee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Virtual SC</Label>
                    <Select value={adminVSC} onValueChange={setAdminVSC}>
                      <SelectTrigger>
                        <SelectValue placeholder="Ja/Nee" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nee">Nee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Aantal DNF</Label>
                    <Select value={adminDnfCount} onValueChange={setAdminDnfCount}>
                      <SelectTrigger>
                        <SelectValue placeholder="0-10" />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                          <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Regen Race</Label>
                    <Select value={adminRainRace} onValueChange={setAdminRainRace}>
                      <SelectTrigger>
                        <SelectValue placeholder="Ja/Nee" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nee">Nee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Sprint Weekend Section */}
                {adminPredRace && races.find(r => r.race_id === adminPredRace)?.is_sprint_weekend && (
                  <div className="border border-purple-500 rounded-lg p-4 bg-purple-500/10" data-testid="admin-sprint-section">
                    <h4 className="font-bold text-purple-400 mb-4 flex items-center">
                      <Zap className="mr-2" size={16} />
                      Sprint Weekend Voorspellingen
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm font-bold text-purple-400">Sprint Race Winnaar</Label>
                        <Select value={adminSprintWinner} onValueChange={setAdminSprintWinner}>
                          <SelectTrigger data-testid="admin-sprint-winner">
                            <SelectValue placeholder="Selecteer coureur" />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((d) => (
                              <SelectItem key={d.driver_id} value={d.driver_id}>
                                {d.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-bold text-purple-400">Sprint Kwalificatie Winnaar</Label>
                        <Select value={adminSprintPoleWinner} onValueChange={setAdminSprintPoleWinner}>
                          <SelectTrigger data-testid="admin-sprint-pole-winner">
                            <SelectValue placeholder="Selecteer coureur" />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((d) => (
                              <SelectItem key={d.driver_id} value={d.driver_id}>
                                {d.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-bold text-purple-400">Sprint Pole Tijd</Label>
                        <Input
                          value={adminSprintPoleTime}
                          onChange={(e) => setAdminSprintPoleTime(e.target.value)}
                          placeholder="MM:SS.mmm"
                          data-testid="admin-sprint-pole-time"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button 
                  onClick={handleAdminSubmitPrediction}
                  className="w-full bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]"
                  disabled={!adminPredUser || !adminPredRace}
                >
                  <CheckCircle className="mr-2" size={16} />
                  Voorspelling Opslaan
                </Button>

                {/* Info Box */}
                <div className="bg-secondary border border-border rounded-lg p-4 text-sm text-muted-foreground">
                  <p className="font-semibold mb-2">ℹ️ Informatie</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Je kunt voorspellingen invoeren namens elke gebruiker (behalve admins)</li>
                    <li>Als admin kun je ook <strong>na de deadline</strong> voorspellingen invoeren</li>
                    <li>Bestaande voorspellingen worden overschreven</li>
                    <li>Vul minimaal de Kwalificatie Top 3 OF Race Top 5 in om op te slaan</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Championship Predictions for User */}
            <Card className="border-2 border-purple-500 mt-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Trophy className="mr-2 text-purple-500" size={20} />
                  Kampioenschap Voorspelling Invoeren voor Gebruiker
                </CardTitle>
                <CardDescription>
                  Voer Wereldkampioen en Constructeurskampioen voorspellingen in namens een gebruiker.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* User Selection */}
                <div>
                  <Label className="font-semibold">Selecteer Gebruiker *</Label>
                  <Select value={adminChampUser} onValueChange={setAdminChampUser}>
                    <SelectTrigger className="max-w-md">
                      <SelectValue placeholder="Selecteer gebruiker" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.filter(u => u.role !== 'super_admin').map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.username} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {adminChampUser && (
                  <>
                    {/* World Champion Top 3 */}
                    <div className="bg-[#1a2332] p-4 rounded-lg border-2 border-purple-500/50">
                      <Label className="font-semibold text-lg mb-3 block text-white">
                        🏆 Wereldkampioen Top 3
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[1, 2, 3].map((pos) => (
                          <div key={pos}>
                            <Label className="text-sm font-bold text-purple-400">
                              {pos === 1 ? '🥇' : pos === 2 ? '🥈' : '🥉'} Positie {pos}
                            </Label>
                            <Select 
                              value={adminChampWC[pos - 1] || ''} 
                              onValueChange={(val) => {
                                const newWC = [...adminChampWC];
                                newWC[pos - 1] = val;
                                setAdminChampWC(newWC);
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={`Selecteer coureur ${pos}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {drivers.map((driver) => (
                                  <SelectItem 
                                    key={driver.driver_id} 
                                    value={driver.driver_id}
                                    disabled={adminChampWC.includes(driver.driver_id) && adminChampWC[pos - 1] !== driver.driver_id}
                                  >
                                    {formatDriverNameLastFirst(driver)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Constructor Champion Top 3 */}
                    <div className="bg-[#1a2332] p-4 rounded-lg border-2 border-red-500/50">
                      <Label className="font-semibold text-lg mb-3 block text-white">
                        🏎️ Constructeurskampioen Top 3
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[1, 2, 3].map((pos) => (
                          <div key={pos}>
                            <Label className="text-sm font-bold text-red-400">
                              {pos === 1 ? '🥇' : pos === 2 ? '🥈' : '🥉'} Positie {pos}
                            </Label>
                            <Select 
                              value={adminChampCC[pos - 1] || ''} 
                              onValueChange={(val) => {
                                const newCC = [...adminChampCC];
                                newCC[pos - 1] = val;
                                setAdminChampCC(newCC);
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={`Selecteer team ${pos}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {teams.map((team) => (
                                  <SelectItem 
                                    key={team.team_id} 
                                    value={team.team_id}
                                    disabled={adminChampCC.includes(team.team_id) && adminChampCC[pos - 1] !== team.team_id}
                                  >
                                    {team.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button 
                      onClick={handleAdminSubmitChampionship}
                      className="w-full bg-purple-600 text-white hover:bg-purple-700"
                      disabled={!adminChampUser || (adminChampWC.filter(Boolean).length === 0 && adminChampCC.filter(Boolean).length === 0)}
                    >
                      <Trophy className="mr-2" size={16} />
                      Kampioenschap Voorspelling Opslaan
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results">
            <Card className="border-2 border-[#FDB913]">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Flag className="mr-2 text-[#FDB913]" size={20} />
                  Race Resultaten Invoeren
                </CardTitle>
                <CardDescription>
                  Voer de volledige race uitslag in voor correcte puntenberekening. Minimaal de top 10 is vereist.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Race Selection - Including Test Races */}
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <Label className="font-semibold">Selecteer Race</Label>
                    <Select value={selectedRace} onValueChange={(val) => {
                      setSelectedRace(val);
                      loadExistingResults(val);
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer race" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[400px]">
                        {/* Test Races Section */}
                        {testRaces && testRaces.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30">
                              🧪 Test Seizoen
                            </div>
                            {testRaces.map((race) => (
                              <SelectItem key={race.race_id} value={race.race_id}>
                                🧪 {race.name} - {race.status === 'completed' ? '✅ Voltooid' : race.status}
                              </SelectItem>
                            ))}
                            <div className="px-2 py-1.5 text-xs font-semibold text-[#FDB913] bg-[#FDB913]/10 mt-1">
                              🏁 Seizoen 2026
                            </div>
                          </>
                        )}
                        {/* Regular Season Races */}
                        {races.map((race) => {
                          const flagUrl = getRaceFlagIcon(race);
                          return (
                            <SelectItem key={race.race_id} value={race.race_id}>
                              <span className="flex items-center">
                                {flagUrl ? (
                                  <img src={flagUrl} alt="" className="inline-block w-5 h-4 mr-2 rounded-sm object-cover" />
                                ) : (
                                  <span className="mr-2">🏁</span>
                                )}
                                {race.name} - {race.status === 'completed' ? '✅ Voltooid' : race.status}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={clearResults}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="mr-2" size={16} />
                    Reset Formulier
                  </Button>
                </div>

                {/* Test Race Indicator */}
                {selectedRace && testRaces?.some(r => r.race_id === selectedRace) && (
                  <div className="bg-orange-100 dark:bg-orange-900/30 border border-orange-400 p-3 rounded-lg">
                    <p className="text-sm text-orange-700 dark:text-orange-300 flex items-center">
                      <TestTube2 className="mr-2" size={16} />
                      <strong>Test Race:</strong>&nbsp;Resultaten worden opgeslagen voor het test seizoen
                    </p>
                  </div>
                )}

                {/* Full Race Positions - All 22 */}
                <div className="bg-[#FDB913]/10 dark:bg-[#FDB913]/20 p-4 rounded-lg border-2 border-[#FDB913]">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="font-semibold text-lg">Volledige Race Uitslag (22 posities)</Label>
                    <span className="text-sm text-muted-foreground">
                      Ingevuld: {racePositions.filter(p => p !== '').length}/22
                    </span>
                  </div>
                  
                  {/* Top 10 - Required */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Top 10 (verplicht)</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2">
                      {Array.from({length: 10}, (_, i) => i + 1).map((pos) => (
                        <div key={pos}>
                          <Label className="text-xs font-bold text-[#FDB913]">P{pos}</Label>
                          <Select 
                            value={racePositions[pos - 1]} 
                            onValueChange={(val) => {
                              const newPositions = [...racePositions];
                              newPositions[pos - 1] = val;
                              setRacePositions(newPositions);
                            }}
                          >
                            <SelectTrigger className="h-9 text-xs">
                              <SelectValue placeholder={`P${pos}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {drivers.map((driver) => (
                                <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                  {formatDriverName(driver.name).split(' ').pop()}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* P11-P22 - Optional but recommended */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">P11-P22 (aanbevolen voor nauwkeurige scoring)</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-12 gap-2">
                      {Array.from({length: 12}, (_, i) => i + 11).map((pos) => (
                        <div key={pos}>
                          <Label className="text-xs text-muted-foreground">P{pos}</Label>
                          <Select 
                            value={racePositions[pos - 1] || "none"} 
                            onValueChange={(val) => {
                              const newPositions = [...racePositions];
                              newPositions[pos - 1] = val === "none" ? '' : val;
                              setRacePositions(newPositions);
                            }}
                          >
                            <SelectTrigger className="h-9 text-xs">
                              <SelectValue placeholder={`P${pos}`} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">- Leeg -</SelectItem>
                              {drivers.map((driver) => (
                                <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                  {formatDriverName(driver.name).split(' ').pop()}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Qualifying Results - Show based on settings (for test races use test season settings) */}
                {(() => {
                  const isTestRace = testRaces?.some(r => r.race_id === selectedRace);
                  const settings = isTestRace && testSeason?.prediction_settings 
                    ? testSeason.prediction_settings 
                    : seasonSettings;
                  const qualEnabled = settings?.qualifying_top3_enabled !== false;
                  const qualP2P3Enabled = settings?.qualifying_p2p3_enabled === true;
                  
                  if (!qualEnabled) return null;
                  
                  return (
                <div className="bg-blue-500/10 dark:bg-blue-500/20 p-4 rounded-lg border-2 border-blue-400 dark:border-blue-500">
                  <Label className="font-semibold mb-3 block">
                    {qualP2P3Enabled ? 'Kwalificatie Top 3' : 'Kwalificatie Pole Position'}
                  </Label>
                  <div className={`grid grid-cols-1 ${qualP2P3Enabled ? 'md:grid-cols-3' : 'md:grid-cols-1 max-w-md'} gap-2`}>
                    {/* P1 - Always shown */}
                    <div>
                      <Label className="text-sm font-bold text-blue-600 dark:text-blue-400">P1 (Pole)</Label>
                      <Select 
                        value={qualTop3[0]} 
                        onValueChange={(val) => {
                          const newTop3 = [...qualTop3];
                          newTop3[0] = val;
                          setQualTop3(newTop3);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="P1" />
                        </SelectTrigger>
                        <SelectContent>
                          {drivers.map((driver) => (
                            <SelectItem key={driver.driver_id} value={driver.driver_id}>
                              {formatDriverNameLastFirst(driver)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* P2 and P3 - Only if qualifying_p2p3_enabled */}
                    {qualP2P3Enabled && (
                    <>
                      <div>
                        <Label className="text-sm font-bold text-blue-600 dark:text-blue-400">P2</Label>
                        <Select 
                          value={qualTop3[1]} 
                          onValueChange={(val) => {
                            const newTop3 = [...qualTop3];
                            newTop3[1] = val;
                            setQualTop3(newTop3);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="P2" />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((driver) => (
                              <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                {formatDriverNameLastFirst(driver)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-bold text-blue-600 dark:text-blue-400">P3</Label>
                        <Select 
                          value={qualTop3[2]} 
                          onValueChange={(val) => {
                            const newTop3 = [...qualTop3];
                            newTop3[2] = val;
                            setQualTop3(newTop3);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="P3" />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((driver) => (
                              <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                {driver.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                    )}
                  </div>
                </div>
                  );
                })()}

                {/* Other Results - conditional based on settings */}
                {(() => {
                  const isTestRace = testRaces?.some(r => r.race_id === selectedRace);
                  const settings = isTestRace && testSeason?.prediction_settings 
                    ? testSeason.prediction_settings 
                    : seasonSettings;
                  
                  return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {settings?.fastest_lap_enabled !== false && (
                  <div>
                    <Label>Snelste Ronde</Label>
                    <Select value={fastestLap} onValueChange={setFastestLap}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer coureur" />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.map((driver) => (
                          <SelectItem key={driver.driver_id} value={driver.driver_id}>
                            {driver.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  )}

                  {settings?.pole_time_enabled !== false && (
                  <div>
                    <Label>Pole Tijd</Label>
                    <Input 
                      placeholder="MM:SS.mmm (bijv. 01:30.456)"
                      value={poleTime}
                      onChange={(e) => setPoleTime(e.target.value)}
                    />
                  </div>
                  )}

                  {settings?.safety_car_enabled !== false && (
                  <div>
                    <Label>Safety Car</Label>
                    <Select value={safetyCar} onValueChange={setSafetyCar}>
                      <SelectTrigger>
                        <SelectValue placeholder="Ja/Nee" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nee">Nee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  )}

                  {settings?.virtual_safety_car_enabled !== false && (
                  <div>
                    <Label>Virtual Safety Car</Label>
                    <Select value={virtualSafetyCar} onValueChange={setVirtualSafetyCar}>
                      <SelectTrigger>
                        <SelectValue placeholder="Ja/Nee" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nee">Nee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  )}

                  {settings?.dnf_count_enabled !== false && (
                  <div>
                    <Label>Aantal DNF</Label>
                    <Select value={dnfCount} onValueChange={setDnfCount}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer" />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                          <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  )}

                  {settings?.rain_race_enabled !== false && (
                  <div>
                    <Label>Regen Race</Label>
                    <Select value={rainRace} onValueChange={setRainRace}>
                      <SelectTrigger>
                        <SelectValue placeholder="Ja/Nee" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nee">Nee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  )}
                </div>
                  );
                })()}

                {/* Sprint Results Section (only for sprint weekends AND if sprint is enabled) */}
                {(() => {
                  const isTestRace = testRaces?.some(r => r.race_id === selectedRace);
                  const settings = isTestRace && testSeason?.prediction_settings 
                    ? testSeason.prediction_settings 
                    : seasonSettings;
                  const currentRace = [...races, ...(testRaces || [])].find(r => r.race_id === selectedRace);
                  
                  if (!settings?.sprint_race_enabled || !currentRace?.is_sprint_weekend) return null;
                  
                  return (
                <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg border-2 border-purple-300 dark:border-purple-600">
                  <h3 className="font-semibold mb-3 flex items-center text-purple-800 dark:text-purple-300">
                    🏎️ Sprint Weekend Resultaten
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-purple-800 dark:text-purple-300">Sprint Race Winnaar (10 pts)</Label>
                      <Select value={sprintWinner} onValueChange={setSprintWinner}>
                        <SelectTrigger className="border-purple-300 dark:border-purple-600">
                          <SelectValue placeholder="Selecteer winnaar" />
                        </SelectTrigger>
                        <SelectContent>
                          {drivers.map((driver) => (
                            <SelectItem key={driver.driver_id} value={driver.driver_id}>
                              {driver.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {seasonSettings.sprint_pole_winner_enabled && (
                    <>
                    <div>
                      <Label className="text-purple-800 dark:text-purple-300">Sprint Kwalificatie Winnaar / Pole (10 pts)</Label>
                      <Select value={sprintPoleWinner} onValueChange={setSprintPoleWinner}>
                        <SelectTrigger className="border-purple-300 dark:border-purple-600">
                          <SelectValue placeholder="Selecteer pole winnaar" />
                        </SelectTrigger>
                        <SelectContent>
                          {drivers.map((driver) => (
                            <SelectItem key={driver.driver_id} value={driver.driver_id}>
                              {driver.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-purple-800 dark:text-purple-300">Sprint Pole Tijd (exact 40 pts, binnen 0.5 sec 10 pts)</Label>
                      <Input 
                        placeholder="MM:SS.mmm (bijv. 01:28.456)"
                        value={sprintPoleTime}
                        onChange={(e) => setSprintPoleTime(e.target.value)}
                        className="border-purple-300 dark:border-purple-600"
                      />
                    </div>
                    </>
                    )}
                  </div>
                </div>
                  );
                })()}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4">
                  <Button 
                    onClick={handleEnterResults}
                    className="flex-1 bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]"
                    disabled={!selectedRace}
                  >
                    <CheckCircle className="mr-2" size={16} />
                    Resultaten Opslaan
                  </Button>
                  <Button 
                    onClick={handleScoreRace}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!selectedRace}
                  >
                    <Trophy className="mr-2" size={16} />
                    Uitslag Opslaan
                  </Button>
                  <Button 
                    onClick={handleRescoreRace}
                    variant="outline"
                    className="flex-1 border-blue-500 text-blue-600 hover:bg-blue-50"
                    disabled={!selectedRace}
                  >
                    <RefreshCw className="mr-2" size={16} />
                    Opnieuw Scoren
                  </Button>
                </div>
                
                {/* Send Newsletter Button */}
                <div className="mt-4">
                  <Button 
                    onClick={handleSendNewsletter}
                    variant="outline"
                    className="w-full border-purple-500 text-purple-600 hover:bg-purple-50"
                    disabled={!selectedRace || sendingNewsletter}
                  >
                    {sendingNewsletter ? (
                      <>
                        <RefreshCw className="mr-2 animate-spin" size={16} />
                        Nieuwsbrief Versturen...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2" size={16} />
                        📧 Verstuur Nieuwsbrief naar Alle Deelnemers
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Verstuurt race resultaten, top 10 stand, grootste stijger/daler en rode lantaarn naar alle gebruikers
                  </p>
                </div>

                {/* Info Box */}
                <div className="bg-secondary border border-border rounded-lg p-4 text-sm text-muted-foreground">
                  <p className="font-semibold mb-2">📋 Hoe werkt de scoring?</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>De volledige uitslag wordt vergeleken met de voorspellingen van elke deelnemer</li>
                    <li>Punten worden toegekend op basis van het verschil tussen voorspelde en werkelijke positie</li>
                    <li>Exact goed = 25 punten, 1 positie verschil = 18 punten, tot 9 posities verschil = 1 punt</li>
                    <li>Zorg dat je minimaal de top 10 invult voor correcte scoring</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Championship Results Card */}
            <Card className="border-2 border-yellow-500">
              <CardHeader className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 dark:from-yellow-500/30 dark:to-yellow-600/20">
                <CardTitle className="flex items-center text-yellow-700 dark:text-yellow-400">
                  <Trophy className="mr-2" size={24} />
                  🏆 Kampioenschap Resultaten
                </CardTitle>
                <CardDescription>
                  Voer de eindresultaten van het seizoen in na de laatste race
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {/* Status Badge */}
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    championshipResults.finalized 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }`}>
                    {championshipResults.finalized ? '✅ Gefinaliseerd' : '⏳ Nog niet gefinaliseerd'}
                  </span>
                  {championshipResults.updated_at && (
                    <span className="text-sm text-muted-foreground">
                      Laatst bijgewerkt: {new Date(championshipResults.updated_at).toLocaleString('nl-NL')}
                    </span>
                  )}
                </div>

                {/* Warning for non-finalized */}
                {!championshipResults.finalized && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 p-4 rounded-lg">
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      <strong>Let op:</strong> De kampioenschap resultaten worden pas definitief nadat u op "Finaliseren & Punten Toekennen" klikt. 
                      Pas na finalisatie worden de punten uitgedeeld aan de deelnemers.
                    </p>
                  </div>
                )}

                {/* World Champion Top 5 */}
                {seasonSettings.world_champion_enabled && (
                <div className="space-y-3">
                  <Label className="text-lg font-semibold flex items-center">
                    👑 Wereldkampioen Eindstand 2026
                    <span className="ml-2 text-sm font-normal text-muted-foreground">(Top 5 voor scoring)</span>
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Vul de top 5 in van de coureurs eindstand. Deelnemers voorspelden de top 3.
                    <br />
                    <span className="text-yellow-600 dark:text-yellow-400">
                      Punten: Exact juist = 50 | 1 positie verschil = 40 | 2 posities verschil = 30
                    </span>
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    {[0, 1, 2, 3, 4].map((idx) => (
                      <div key={`wdc-${idx}`}>
                        <Label className="text-xs text-muted-foreground mb-1 block">
                          P{idx + 1} {idx === 0 && '(Kampioen)'}
                        </Label>
                        <Select 
                          value={wdcTop5[idx] || 'none'} 
                          onValueChange={(val) => {
                            const newTop5 = [...wdcTop5];
                            newTop5[idx] = val === 'none' ? '' : val;
                            setWdcTop5(newTop5);
                          }}
                          disabled={championshipResults.finalized}
                        >
                          <SelectTrigger className={idx < 3 ? 'border-yellow-400' : ''}>
                            <SelectValue placeholder={`P${idx + 1}`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-- Leeg --</SelectItem>
                            {drivers.map((driver) => (
                              <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                {driver.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Constructor Champion Top 5 */}
                {seasonSettings.constructor_champion_enabled && (
                <div className="space-y-3">
                  <Label className="text-lg font-semibold flex items-center">
                    🏎️ Constructeurs Kampioen Eindstand 2026
                    <span className="ml-2 text-sm font-normal text-muted-foreground">(Top 5 voor scoring)</span>
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Vul de top 5 in van de constructeurs eindstand. Deelnemers voorspelden de top 3.
                    <br />
                    <span className="text-yellow-600 dark:text-yellow-400">
                      Punten: Exact juist = 50 | 1 positie verschil = 40 | 2 posities verschil = 30
                    </span>
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    {[0, 1, 2, 3, 4].map((idx) => (
                      <div key={`wcc-${idx}`}>
                        <Label className="text-xs text-muted-foreground mb-1 block">
                          P{idx + 1} {idx === 0 && '(Kampioen)'}
                        </Label>
                        <Select 
                          value={wccTop5[idx] || 'none'} 
                          onValueChange={(val) => {
                            const newTop5 = [...wccTop5];
                            newTop5[idx] = val === 'none' ? '' : val;
                            setWccTop5(newTop5);
                          }}
                          disabled={championshipResults.finalized}
                        >
                          <SelectTrigger className={idx < 3 ? 'border-yellow-400' : ''}>
                            <SelectValue placeholder={`P${idx + 1}`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-- Leeg --</SelectItem>
                            {teams.map((team) => (
                              <SelectItem key={team.team_id || team.name} value={team.team_id || team.name}>
                                {team.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Action Buttons */}
                {!championshipResults.finalized && (
                <div className="flex flex-wrap gap-3 pt-4">
                  <Button
                    onClick={handleSaveChampionshipResults}
                    variant="outline"
                    className="border-yellow-500 text-yellow-700 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-900/20"
                    disabled={savingChampionship}
                  >
                    {savingChampionship ? (
                      <RefreshCw className="mr-2 animate-spin" size={16} />
                    ) : (
                      <Save className="mr-2" size={16} />
                    )}
                    Opslaan (zonder finalisatie)
                  </Button>
                  <Button
                    onClick={handleFinalizeChampionship}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    disabled={savingChampionship || (!wdcTop5[0] && !wccTop5[0])}
                  >
                    {savingChampionship ? (
                      <RefreshCw className="mr-2 animate-spin" size={16} />
                    ) : (
                      <Trophy className="mr-2" size={16} />
                    )}
                    Finaliseren & Punten Toekennen
                  </Button>
                </div>
                )}

                {/* Finalized Message */}
                {championshipResults.finalized && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 p-4 rounded-lg">
                  <p className="text-green-700 dark:text-green-400 font-semibold mb-2">✅ Kampioenschap Gefinaliseerd</p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    De resultaten zijn definitief en de punten zijn uitgedeeld aan alle deelnemers die correct voorspeld hebben.
                  </p>
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Test Season Tab */}
          <TabsContent value="test-season">
            <div className="space-y-6">
              {/* Info Card */}
              <Card className="border-2 border-orange-400">
                <CardHeader className="bg-orange-500/10 dark:bg-orange-500/20">
                  <CardTitle className="flex items-center text-orange-700 dark:text-orange-400">
                    <TestTube2 className="mr-2" size={24} />
                    🧪 Test Seizoen - Pre-Season Testing 2026
                  </CardTitle>
                  <CardDescription>Laat gebruikers de pool uitproberen met de F1 pre-season testdagen</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="bg-orange-500/10 dark:bg-orange-500/20 p-4 rounded-lg mb-4 border border-orange-400/50">
                    <h4 className="font-bold text-orange-700 dark:text-orange-400 mb-2">Pre-Season Test Weekenden 2026:</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center">
                        <span className="bg-orange-500 text-white px-2 py-1 rounded mr-3 text-xs">1</span>
                        <strong>Barcelona Test (Privé)</strong> - 26-30 januari 2026 (5 dagen, achter gesloten deuren)
                      </li>
                      <li className="flex items-center">
                        <span className="bg-orange-500 text-white px-2 py-1 rounded mr-3 text-xs">2</span>
                        <strong>Bahrain Test 1</strong> - 11-13 februari 2026 (3 dagen, officieel)
                      </li>
                      <li className="flex items-center">
                        <span className="bg-orange-500 text-white px-2 py-1 rounded mr-3 text-xs">3</span>
                        <strong>Bahrain Test 2</strong> - 18-20 februari 2026 (3 dagen, officieel)
                      </li>
                    </ul>
                  </div>
                  
                  <p className="text-muted-foreground text-sm mb-4">
                    Het test seizoen is een apart seizoen waar gebruikers kunnen oefenen met voorspellingen.
                    De punten en voorspellingen staan los van het echte seizoen en kunnen op elk moment worden verwijderd.
                  </p>
                </CardContent>
              </Card>

              {/* Test Season Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    {testSeason ? (
                      <CheckCircle className="mr-2 text-green-500" size={24} />
                    ) : (
                      <AlertTriangle className="mr-2 text-yellow-500" size={24} />
                    )}
                    Test Seizoen Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {testSeason ? (
                    <div className="space-y-4">
                      <div className="bg-green-500/10 dark:bg-green-500/20 border border-green-500/50 p-4 rounded-lg">
                        <p className="text-green-700 dark:text-green-400 font-semibold">✅ Test seizoen is actief</p>
                        <p className="text-sm text-green-600 dark:text-green-500 mt-1">Gebruikers kunnen voorspellingen doen voor de test events</p>
                      </div>
                      
                      {/* Test Events */}
                      <div>
                        <h4 className="font-semibold mb-2">Test Events ({testRaces.length}):</h4>
                        <div className="space-y-2">
                          {testRaces.map((race) => (
                            <div key={race.race_id} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                              <div>
                                <p className="font-medium">{race.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {race.location}, {race.country} • {race.date}
                                </p>
                              </div>
                              <span className={`px-2 py-1 rounded text-xs ${race.status === 'completed' ? 'bg-green-500/20 text-green-700 dark:text-green-400' : 'bg-blue-500/20 text-blue-700 dark:text-blue-400'}`}>
                                {race.status === 'completed' ? 'Voltooid' : 'Aankomend'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Delete Button */}
                      <div className="pt-4 border-t">
                        <Button 
                          onClick={handleDeleteTestSeason}
                          variant="destructive"
                          className="w-full"
                          disabled={deletingTestSeason}
                        >
                          {deletingTestSeason ? (
                            <>
                              <RefreshCw className="mr-2 animate-spin" size={16} />
                              Verwijderen...
                            </>
                          ) : (
                            <>
                              <Trash2 className="mr-2" size={16} />
                              🗑️ Test Seizoen en Alle Data Verwijderen
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                          Dit verwijdert alle test voorspellingen, resultaten en races permanent
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-yellow-500/10 dark:bg-yellow-500/20 border border-yellow-500/50 p-4 rounded-lg">
                        <p className="text-yellow-700 dark:text-yellow-400 font-semibold">⚠️ Geen test seizoen actief</p>
                        <p className="text-sm text-yellow-600 dark:text-yellow-500 mt-1">Maak een test seizoen aan om gebruikers te laten oefenen</p>
                      </div>
                      
                      <Button 
                        onClick={handleCreateTestSeason}
                        className="w-full bg-orange-500 hover:bg-orange-600"
                        disabled={creatingTestSeason}
                      >
                        {creatingTestSeason ? (
                          <>
                            <RefreshCw className="mr-2 animate-spin" size={16} />
                            Aanmaken...
                          </>
                        ) : (
                          <>
                            <Plus className="mr-2" size={16} />
                            🧪 Test Seizoen Aanmaken
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        Dit maakt 3 test events aan met dezelfde instellingen als het reguliere seizoen (gratis deelname)
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Instructions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Info className="mr-2 text-blue-500" size={24} />
                    Hoe werkt het test seizoen?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start">
                      <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2 mt-0.5 text-xs flex-shrink-0">1</span>
                      <span>Maak het test seizoen aan - dit kopieert alle coureurs en teams van het reguliere seizoen</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2 mt-0.5 text-xs flex-shrink-0">2</span>
                      <span>Gebruikers kunnen op de Voorspel pagina kiezen voor het test seizoen</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2 mt-0.5 text-xs flex-shrink-0">3</span>
                      <span>Voorspellingen voor test events staan volledig los van het echte seizoen</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2 mt-0.5 text-xs flex-shrink-0">4</span>
                      <span>Je kunt resultaten invoeren voor de test events en punten laten berekenen</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2 mt-0.5 text-xs flex-shrink-0">5</span>
                      <span>Verwijder het test seizoen wanneer je klaar bent - alle test data wordt gewist</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Help Tab */}
          <TabsContent value="help">
            <HelpTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
